import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  X, MapPin, Navigation, Car, Bike, Footprints, Bus, Route, XCircle, RefreshCw,
  Football, Soccer, Basketball, Tennis, Volleyball, Dumbbell, Activity, Running,
  Book, BookOpen, Utensils, Coffee, Wine, Music, Camera, Palette, Gamepad2,
  Users, Heart, Sparkles, GraduationCap, TreePine, Waves, Mountain, Car as CarIcon,
  Upload, Send
} from 'lucide-react';
import api from '../config/api';
import HobbyFilterIcon from './HobbyFilterIcons';
import { getEventCategoryIconSvgTabler, getTablerIconSvgByName, getEventTablerIconName, EVENT_TABLER_ICON_TAGS } from '../utils/eventCategoryTablerIcons';

// Helper function to calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

// Fetch directions from Mapbox Directions API
const fetchDirections = async (start, end, profile, mapboxToken) => {
  if (!start || !end || !mapboxToken) return null;
  
  try {
    const [startLng, startLat] = start;
    const [endLng, endLat] = end;
    const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${startLng},${startLat};${endLng},${endLat}?geometries=geojson&steps=true&access_token=${mapboxToken}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Directions API error:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    if (data.routes && data.routes.length > 0) {
      return {
        geometry: data.routes[0].geometry,
        duration: data.routes[0].duration, // in seconds
        distance: data.routes[0].distance // in meters
      };
    }
    return null;
  } catch (error) {
    console.error('Directions error:', error);
    return null;
  }
};

// Helper: download image when possible, copy caption, open Google Photos (so user can upload the downloaded file)
const downloadImageAndOpenGooglePhotos = async (imageSrc, filename, caption) => {
  const safeName = (filename || 'ClubFinder').replace(/[^a-zA-Z0-9-_.\s]/g, '').slice(0, 60) + '.jpg';
  try {
    if (imageSrc && (imageSrc.startsWith('data:') || imageSrc.startsWith('http'))) {
      if (imageSrc.startsWith('data:')) {
        const a = document.createElement('a');
        a.href = imageSrc;
        a.download = safeName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        const res = await fetch(imageSrc, { mode: 'cors' });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = safeName;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }
    }
  } catch (_) { /* CORS or fetch failed — no download */ }
  try { await navigator.clipboard.writeText(caption); } catch (_) {}
  window.open('https://photos.google.com/', '_blank', 'noopener');
};

// Geocode address using Mapbox Geocoding API
const geocodeAddress = async (address, mapboxToken) => {
  if (!address || !mapboxToken) return null;
  
  try {
    const query = encodeURIComponent(address.trim());
    if (!query) return null;
    
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapboxToken}&limit=1&types=address,poi,place`
    );
    
    if (!response.ok) {
      console.error('Geocoding API error:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      // Validate coordinates
      if (typeof lng === 'number' && typeof lat === 'number' && 
          lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
        return [lng, lat];
      }
    }
    return null;
  } catch (error) {
    console.error('Geocoding error for address:', address, error);
    return null;
  }
};

// Get light preset based on local time of day (Dawn, Day, Dusk, Night)
const getLightPresetFromTime = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 7) return 'dawn';
  if (hour >= 7 && hour < 17) return 'day';
  if (hour >= 17 && hour < 20) return 'dusk';
  return 'night'; // 20-24 and 0-5
};

// Get group avatar URL (matching Friends.js logic)
const getGroupAvatarSrc = (group) => {
  if (!group) return 'https://via.placeholder.com/50';
  // Check for uploaded image first
  if (group.avatar || group.image) {
    return group.avatar || group.image;
  }
  if (group.id === 1029 || group.name?.toLowerCase() === 'hiker8') {
    return 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=200&h=200&fit=crop';
  }
  if (group.id === 1030 || group.name?.toLowerCase() === 'fitcrew') {
    return 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=200&h=200&fit=crop';
  }
  if (group.id === 1031 || group.name?.toLowerCase() === 'artists') {
    return 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=200&h=200&fit=crop';
  }
  if (group.id === 1032 || group.name?.toLowerCase() === 'techies') {
    return 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200&h=200&fit=crop';
  }
  if (group.id === 1033 || group.name?.toLowerCase() === 'foodies') {
    return 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=200&h=200&fit=crop';
  }
  if (group.id === 1034 || group.name?.toLowerCase() === 'gamers') {
    return 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=200&h=200&fit=crop';
  }
  if (group.id === 1035 || group.name?.toLowerCase() === 'bookworms') {
    return 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=200&h=200&fit=crop';
  }
  return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=200&h=200&fit=crop';
};

// Icon cache - stores hobby -> icon name mapping (persists across renders)
const iconCache = new Map();

// Available lucide-react icons for hobbies (keywords only - icons are rendered via SVG paths)
const availableIcons = {
  // Sports
  'Soccer': { keywords: ['football', 'soccer', 'futbol'] },
  'Basketball': { keywords: ['basketball', 'hoops'] },
  'Tennis': { keywords: ['tennis', 'racket'] },
  'Volleyball': { keywords: ['volleyball'] },
  'Bike': { keywords: ['cycling', 'bike', 'bicycle'] },
  'Running': { keywords: ['running', 'fitness', 'gym', 'exercise'] },
  'Mountain': { keywords: ['hiking', 'mountain', 'climbing'] },
  'Football': { keywords: ['football', 'american football'] },
  'Dumbbell': { keywords: ['weightlifting', 'strength', 'workout'] },
  'Activity': { keywords: ['sports', 'athletics'] },
  
  // Food & Drink
  'Utensils': { keywords: ['food', 'cooking', 'restaurant', 'dining'] },
  'Coffee': { keywords: ['coffee', 'cafe'] },
  'Wine': { keywords: ['wine', 'drink', 'bar'] },
  
  // Arts & Creative
  'Palette': { keywords: ['art', 'painting', 'drawing', 'creative'] },
  'Music': { keywords: ['music', 'singing', 'concert', 'band'] },
  'Camera': { keywords: ['photography', 'camera', 'photo'] },
  
  // Entertainment
  'Gamepad2': { keywords: ['game', 'gaming', 'video games'] },
  
  // Education
  'Book': { keywords: ['book', 'reading', 'study', 'literature'] },
  'BookOpen': { keywords: ['reading', 'library'] },
  'GraduationCap': { keywords: ['university', 'college', 'school', 'education'] },
  
  // Nature
  'TreePine': { keywords: ['garden', 'plant', 'nature', 'outdoor'] },
  'Waves': { keywords: ['beach', 'surf', 'ocean', 'water'] },
  
  // Social
  'Heart': { keywords: ['spiritual', 'religion', 'faith', 'church'] },
  'Users': { keywords: ['social', 'community', 'group'] },
  
  // Default
  'MapPin': { keywords: ['location', 'place'] }
};

// Get category icon name based on group category or hobbies
const getCategoryIcon = (group) => {
  const category = (group.category || '').toLowerCase();
  const name = (group.name || '').toLowerCase();
  const hobbies = Array.isArray(group.hobbies) ? group.hobbies.map(h => h.toLowerCase()) : [];
  const allText = [category, name, ...hobbies].join(' ');

  // Check cache first
  const cacheKey = allText;
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }

  // Find best matching icon
  let bestMatch = 'Users';
  let bestScore = 0;

  for (const [iconName, iconData] of Object.entries(availableIcons)) {
    const score = iconData.keywords.reduce((acc, keyword) => {
      if (allText.includes(keyword)) {
        return acc + 1;
      }
      return acc;
    }, 0);
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = iconName;
    }
  }

  // Cache the result
  iconCache.set(cacheKey, bestMatch);
  return bestMatch;
};

// Get SVG path data - GTA-style mission logo icons (bold, geometric, stylized)
const getIconSVGPath = (iconName) => {
  // GTA-style mission logo icons - bold, geometric, high contrast
  const iconPaths = {
    'Soccer': '<path fill="none" d="M12 2 L22 12 L12 22 L2 12 Z" fill="currentColor"/><circle fill="none" cx="12" cy="12" r="8" fill="none" stroke="white" stroke-width="2.5"/><path fill="none" d="M12 4 L12 20 M4 12 L20 12" stroke="white" stroke-width="2.5"/>',
    'Basketball': '<path fill="none" d="M12 2 L22 12 L12 22 L2 12 Z" fill="currentColor"/><circle fill="none" cx="12" cy="12" r="8" fill="none" stroke="white" stroke-width="2.5"/><path fill="none" d="M12 4 Q16 8 12 12 Q8 8 12 4 M12 12 Q16 16 12 20 Q8 16 12 12" stroke="white" stroke-width="2.5"/>',
    'Tennis': '<path fill="none" d="M12 2 L22 12 L12 22 L2 12 Z" fill="currentColor"/><circle fill="none" cx="12" cy="12" r="7" fill="none" stroke="white" stroke-width="3"/><path fill="none" d="M8 8 L16 16 M16 8 L8 16" stroke="white" stroke-width="3"/>',
    'Volleyball': '<path fill="none" d="M12 2 L22 12 L12 22 L2 12 Z" fill="currentColor"/><circle fill="none" cx="12" cy="12" r="8" fill="none" stroke="white" stroke-width="2.5"/><path fill="none" d="M6 6 L18 18 M18 6 L6 18 M12 4 L12 20 M4 12 L20 12" stroke="white" stroke-width="2.5"/>',
    'Bike': '<path fill="none" d="M2 18 L8 18 L10 8 L14 8 L16 18 L22 18" fill="currentColor"/><circle fill="none" cx="6" cy="18" r="4" fill="white"/><circle fill="none" cx="18" cy="18" r="4" fill="white"/><path fill="none" d="M10 8 L14 8 L12 4 Z" fill="white"/>',
    'Running': '<path fill="none" d="M4 4 L20 4 L18 20 L6 20 Z" fill="currentColor"/><circle fill="none" cx="14" cy="6" r="2.5" fill="white"/><path fill="none" d="M12 10 L16 10 L14 18 L10 18 Z" fill="white"/><path fill="none" d="M8 18 L16 18" stroke="white" stroke-width="2"/>',
    'Mountain': '<path fill="none" d="M2 20 L12 4 L22 20 Z" fill="currentColor"/><path fill="none" d="M8 16 L12 10 L16 16" fill="white"/><path fill="none" d="M6 18 L18 18" stroke="white" stroke-width="2"/>',
    'Utensils': '<path fill="none" d="M4 2 L8 2 L8 10 L6 10 L6 20 L10 20 L10 10 L8 10 L8 2 L12 2" fill="currentColor"/><path fill="none" d="M16 2 L20 2 L20 8 L22 8 L22 20 L18 20 L18 8 L20 8 L20 2" fill="currentColor"/>',
    'Coffee': '<path fill="none" d="M4 8 L20 8 L20 18 L4 18 Z" fill="currentColor"/><path fill="none" d="M18 6 L20 6 L20 8" fill="white"/><path fill="none" d="M6 10 L18 10" stroke="white" stroke-width="2"/><circle fill="none" cx="12" cy="13" r="3" fill="white"/>',
    'Wine': '<path fill="none" d="M8 8 L16 8 L16 16 L12 20 L8 16 Z" fill="currentColor"/><path fill="none" d="M10 10 L14 10 L14 14 L12 16 L10 14 Z" fill="white"/><path fill="none" d="M12 8 L12 4" stroke="white" stroke-width="2"/>',
    'Palette': '<path fill="none" d="M12 2 L20 8 L12 14 L4 8 Z" fill="currentColor"/><circle fill="none" cx="12" cy="8" r="5" fill="white"/><circle fill="none" cx="10" cy="6" r="1.5" fill="currentColor"/><circle fill="none" cx="14" cy="6" r="1.5" fill="currentColor"/><circle fill="none" cx="12" cy="10" r="1.5" fill="currentColor"/>',
    'Music': '<path fill="none" d="M6 4 L18 4 L18 18 L6 18 Z" fill="currentColor"/><path fill="none" d="M8 6 L16 6 L16 16 L8 16 Z" fill="white"/><circle fill="none" cx="10" cy="18" r="2" fill="currentColor"/><circle fill="none" cx="16" cy="18" r="2" fill="currentColor"/>',
    'Camera': '<path fill="none" d="M6 6 L18 6 L20 10 L20 18 L4 18 L4 10 Z" fill="currentColor"/><path fill="none" d="M8 8 L16 8 L17 10 L7 10 Z" fill="white"/><circle fill="none" cx="12" cy="14" r="3" fill="white"/><circle fill="none" cx="12" cy="14" r="1.5" fill="currentColor"/>',
    'Gamepad2': '<path fill="none" d="M4 8 L20 8 L20 16 L4 16 Z" fill="currentColor"/><path fill="none" d="M6 10 L10 10 M8 8 L8 12" stroke="white" stroke-width="2"/><path fill="none" d="M14 12 L18 12 M16 10 L16 14" stroke="white" stroke-width="2"/>',
    'Book': '<path fill="none" d="M6 4 L18 4 L18 20 L6 20 Z" fill="currentColor"/><path fill="none" d="M8 6 L16 6 M8 10 L16 10 M8 14 L12 14" stroke="white" stroke-width="2"/><path fill="none" d="M6 4 L6 20" stroke="white" stroke-width="2.5"/>',
    'BookOpen': '<path fill="none" d="M4 4 L12 4 L12 20 L4 20 Z" fill="currentColor"/><path fill="none" d="M20 4 L12 4 L12 20 L20 20 Z" fill="currentColor"/><path fill="none" d="M6 8 L10 8 M6 12 L10 12 M6 16 L10 16" stroke="white" stroke-width="2"/><path fill="none" d="M18 8 L14 8 M18 12 L14 12 M18 16 L14 16" stroke="white" stroke-width="2"/>',
    'GraduationCap': '<path fill="none" d="M4 10 L12 6 L20 10 L12 14 Z" fill="currentColor"/><path fill="none" d="M6 12 L18 12 L18 18 L6 18 Z" fill="currentColor"/><path fill="none" d="M8 10 L16 10" stroke="white" stroke-width="2"/><path fill="none" d="M12 6 L12 14" stroke="white" stroke-width="2"/>',
    'TreePine': '<path fill="none" d="M12 2 L16 8 L8 8 Z" fill="currentColor"/><path fill="none" d="M12 8 L16 14 L8 14 Z" fill="currentColor"/><path fill="none" d="M12 14 L14 18 L10 18 Z" fill="currentColor"/><path fill="none" d="M12 18 L12 22" stroke="white" stroke-width="3"/>',
    'Waves': '<path fill="none" d="M2 8 L6 6 L10 8 L14 6 L18 8 L22 6" fill="currentColor"/><path fill="none" d="M2 12 L6 10 L10 12 L14 10 L18 12 L22 10" fill="currentColor"/><path fill="none" d="M2 16 L6 14 L10 16 L14 14 L18 16 L22 14" fill="currentColor"/>',
    'Heart': '<path fill="none" d="M12 4 L16 8 L20 4 L16 12 L12 18 L8 12 L4 8 Z" fill="currentColor"/><path fill="none" d="M12 8 L14 10 L16 8 L14 12 L12 14 L10 12 Z" fill="white"/>',
    'Users': '<path fill="none" d="M8 6 L16 6 L18 18 L6 18 Z" fill="currentColor"/><circle fill="none" cx="10" cy="4" r="2" fill="white"/><circle fill="none" cx="14" cy="4" r="2" fill="white"/><path fill="none" d="M8 10 L16 10" stroke="white" stroke-width="2"/>',
    'Football': '<ellipse cx="12" cy="12" rx="8" ry="10" fill="currentColor"/><path fill="none" d="M12 4 L12 20 M4 12 L20 12" stroke="white" stroke-width="3"/><path fill="none" d="M8 8 L16 16 M16 8 L8 16" stroke="white" stroke-width="2.5"/>',
    'Dumbbell': '<rect fill="none" x="4" y="10" width="6" height="4" rx="1" fill="currentColor"/><rect fill="none" x="14" y="10" width="6" height="4" rx="1" fill="currentColor"/><path fill="none" d="M10 12 L14 12" stroke="white" stroke-width="3"/>',
    'Activity': '<path fill="none" d="M4 4 L20 4 L20 20 L4 20 Z" fill="currentColor"/><path fill="none" d="M6 12 L10 8 L14 16 L18 12" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/><circle fill="none" cx="10" cy="8" r="2" fill="white"/><circle fill="none" cx="14" cy="16" r="2" fill="white"/>',
    'MapPin': '<path fill="none" d="M12 2 L18 8 L12 14 L6 8 Z" fill="currentColor"/><circle fill="none" cx="12" cy="8" r="3" fill="white"/><path fill="none" d="M12 14 L12 20" stroke="white" stroke-width="2"/>'
  };

  return iconPaths[iconName] || iconPaths['Users'];
};

// Generate SVG icon for marker
const generateCategoryIconSVG = (iconName, size = 24) => {
  const iconPaths = {
    'Soccer': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Basketball': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Tennis': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Volleyball': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Bike': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Running': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Mountain': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Utensils': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Coffee': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Wine': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Palette': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Music': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Camera': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Gamepad2': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Book': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'GraduationCap': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'TreePine': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Waves': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Heart': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'Users': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'
  };
  
  // For now, return a simple circle with icon name - we'll use lucide-react icons via React component
  return iconName;
};

const MEMORY_PINS_KEY = (userId) => `profileMapPins_${userId || 'guest'}`;
const loadMemoryPins = (userId) => {
  try {
    const raw = localStorage.getItem(MEMORY_PINS_KEY(userId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch { return []; }
};
const saveMemoryPins = (userId, pins) => {
  try { localStorage.setItem(MEMORY_PINS_KEY(userId), JSON.stringify(pins)); } catch (e) { console.warn('Could not save map pins', e); }
};
const NOTES_KEY = (userId) => `profileMapNotes_${userId || 'guest'}`;
const loadNotes = (userId) => {
  try {
    const raw = localStorage.getItem(NOTES_KEY(userId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch { return []; }
};
const saveNotes = (userId, notes) => {
  try { localStorage.setItem(NOTES_KEY(userId), JSON.stringify(notes)); } catch (e) { console.warn('Could not save notes', e); }
};
const memoryTypeConfig = {
  club: { label: 'Club', color: '#3b82f6', border: '2px solid #2563eb' },
  hangout: { label: 'Hangout', color: '#eab308', border: '2px solid #ca8a04' },
  event: { label: 'Event', color: '#22c55e', border: '2px solid #16a34a' },
};

// Event category → pin/popup colors (must match server EVENT_CATEGORIES)
export const EVENT_CATEGORY_COLORS = {
  'Sport & Fitness': { bg: '#22c55e', light: '#4ade80', border: '#16a34a', shadow: 'rgba(34,197,94,0.45)' },
  'Music & Concert': { bg: '#ec4899', light: '#f472b6', border: '#db2777', shadow: 'rgba(236,72,153,0.45)' },
  'Networking & Professional': { bg: '#0ea5e9', light: '#38bdf8', border: '#0284c7', shadow: 'rgba(14,165,233,0.45)' },
  'Arts & Culture': { bg: '#a855f7', light: '#c084fc', border: '#9333ea', shadow: 'rgba(168,85,247,0.45)' },
  'Comedy & Entertainment': { bg: '#f59e0b', light: '#fbbf24', border: '#d97706', shadow: 'rgba(245,158,11,0.45)' },
  'Food & Drink': { bg: '#ef4444', light: '#f87171', border: '#dc2626', shadow: 'rgba(239,68,68,0.45)' },
  'Tech & Startup': { bg: '#6366f1', light: '#818cf8', border: '#4f46e5', shadow: 'rgba(99,102,241,0.45)' },
  'Education & Workshop': { bg: '#14b8a6', light: '#2dd4bf', border: '#0d9488', shadow: 'rgba(20,184,166,0.45)' },
  'Family & Kids': { bg: '#f97316', light: '#fb923c', border: '#ea580c', shadow: 'rgba(249,115,22,0.45)' },
  'Volunteering & Community': { bg: '#84cc16', light: '#a3e635', border: '#65a30d', shadow: 'rgba(132,204,22,0.45)' },
  'Gaming & Esports': { bg: '#8b5cf6', light: '#a78bfa', border: '#7c3aed', shadow: 'rgba(139,92,246,0.45)' },
  'Outdoor & Adventure': { bg: '#059669', light: '#10b981', border: '#047857', shadow: 'rgba(5,150,105,0.45)' },
  'Business & Career': { bg: '#475569', light: '#64748b', border: '#334155', shadow: 'rgba(71,85,105,0.45)' },
  'Film & Cinema': { bg: '#1e293b', light: '#334155', border: '#0f172a', shadow: 'rgba(30,41,59,0.5)' },
  'Theater & Performing Arts': { bg: '#be185d', light: '#e11d48', border: '#9d174d', shadow: 'rgba(190,24,93,0.45)' },
  'Nightlife & Party': { bg: '#7c3aed', light: '#8b5cf6', border: '#6d28d9', shadow: 'rgba(124,58,237,0.45)' },
  'Wellness & Mindfulness': { bg: '#06b6d4', light: '#22d3ee', border: '#0891b2', shadow: 'rgba(6,182,212,0.45)' },
  'Book & Reading': { bg: '#b45309', light: '#d97706', border: '#92400e', shadow: 'rgba(180,83,9,0.45)' },
  'Photography & Creative': { bg: '#d946ef', light: '#e879f9', border: '#c026d3', shadow: 'rgba(217,70,239,0.45)' },
  'Travel & Exploration': { bg: '#0d9488', light: '#14b8a6', border: '#0f766e', shadow: 'rgba(13,148,136,0.45)' },
  'Fashion & Style': { bg: '#e11d48', light: '#f43f5e', border: '#be123c', shadow: 'rgba(225,29,72,0.45)' },
  'Science & Innovation': { bg: '#2563eb', light: '#3b82f6', border: '#1d4ed8', shadow: 'rgba(37,99,235,0.45)' },
  'Religion & Spirituality': { bg: '#4f46e5', light: '#6366f1', border: '#4338ca', shadow: 'rgba(79,70,229,0.45)' },
  'Politics & Activism': { bg: '#dc2626', light: '#ef4444', border: '#b91c1c', shadow: 'rgba(220,38,38,0.45)' },
  'Dating & Social': { bg: '#db2777', light: '#ec4899', border: '#be185d', shadow: 'rgba(219,39,119,0.45)' },
  'Hobby & Craft': { bg: '#ca8a04', light: '#eab308', border: '#a16207', shadow: 'rgba(202,138,4,0.45)' },
  'Conference & Summit': { bg: '#0369a1', light: '#0ea5e9', border: '#0284c7', shadow: 'rgba(3,105,161,0.45)' },
  'Festival & Fair': { bg: '#c026d3', light: '#d946ef', border: '#a21caf', shadow: 'rgba(192,38,211,0.45)' },
  'Fundraiser & Charity': { bg: '#15803d', light: '#22c55e', border: '#166534', shadow: 'rgba(21,128,61,0.45)' },
  'Other': { bg: '#64748b', light: '#94a3b8', border: '#475569', shadow: 'rgba(100,116,139,0.45)' },
};

// Event category → icon SVG (24x24, white stroke) for pin and popup
export const EVENT_CATEGORY_ICONS = {
  'Sport & Fitness': '<circle fill="none" cx="12" cy="5" r="2.5" stroke="white" stroke-width="2"/><path fill="none" d="M12 7.5v5.5M12 13l-3 7M12 13l3 7M10 10l-5 9M5 19l-3 0" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Music & Concert': '<path fill="none" d="M9 18V5l12-2v13M9 9l12-2" stroke="white" stroke-width="2" stroke-linecap="round"/><circle fill="none" cx="6" cy="18" r="3" fill="none" stroke="white" stroke-width="2"/><circle fill="none" cx="18" cy="16" r="3" fill="none" stroke="white" stroke-width="2"/>',
  'Networking & Professional': '<path fill="none" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Arts & Culture': '<circle fill="none" cx="12" cy="12" r="10" stroke="white" stroke-width="2"/><path fill="none" d="M12 6v2M12 16v2M6 12h2M16 12h2" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Comedy & Entertainment': '<path fill="none" d="M12 2a3 3 0 0 1 3 3v2a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z" stroke="white" stroke-width="2" stroke-linecap="round"/><path fill="none" d="M8 22s2-4 4-4 4 4 4 4" stroke="white" stroke-width="2" stroke-linecap="round"/><path fill="none" d="M9 11h6" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Food & Drink': '<path fill="none" d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M17 2v7c0 1.1-.9 2-2 2h-4a2 2 0 0 1-2-2V2" stroke="white" stroke-width="2" stroke-linecap="round"/><path fill="none" d="M8 22h8M12 11v11" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Tech & Startup': '<rect fill="none" x="2" y="4" width="20" height="14" rx="2" stroke="white" stroke-width="2"/><path fill="none" d="M2 15h20M8 4V2M16 4V2" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Education & Workshop': '<path fill="none" d="M4 19v-6a8 8 0 0 1 16 0v6M12 5v14" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Family & Kids': '<path fill="none" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Volunteering & Community': '<path fill="none" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Gaming & Esports': '<rect fill="none" x="6" y="10" width="12" height="8" rx="1" stroke="white" stroke-width="2"/><path fill="none" d="M8 10V8a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Outdoor & Adventure': '<path fill="none" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Business & Career': '<rect fill="none" x="2" y="7" width="20" height="14" rx="2" stroke="white" stroke-width="2"/><path fill="none" d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Film & Cinema': '<rect fill="none" x="2" y="2" width="20" height="20" rx="2" stroke="white" stroke-width="2"/><path fill="none" d="M7 2v20M17 2v20M2 12h20" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Theater & Performing Arts': '<path fill="none" d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" stroke="white" stroke-width="2" stroke-linecap="round"/><circle fill="none" cx="12" cy="12" r="3" stroke="white" stroke-width="2"/>',
  'Nightlife & Party': '<path fill="none" d="M8 2h8v6l4 10H4L8 8V2z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path fill="none" d="M12 8v4" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Wellness & Mindfulness': '<path fill="none" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Book & Reading': '<path fill="none" d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="white" stroke-width="2" stroke-linecap="round"/><path fill="none" d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="white" stroke-width="2" stroke-linecap="round"/><path fill="none" d="M8 7h8M8 11h8" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Photography & Creative': '<path fill="none" d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle fill="none" cx="12" cy="13" r="4" stroke="white" stroke-width="2"/>',
  'Travel & Exploration': '<path fill="none" d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle fill="none" cx="12" cy="10" r="3" stroke="white" stroke-width="2"/>',
  'Fashion & Style': '<path fill="none" d="M20.38 3.46L16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Science & Innovation': '<path fill="none" d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path fill="none" d="M14 2v6h6M12 18v-4M10 14h4" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Religion & Spirituality': '<circle fill="none" cx="12" cy="12" r="10" stroke="white" stroke-width="2"/><path fill="none" d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Politics & Activism': '<path fill="none" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Dating & Social': '<path fill="none" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Hobby & Craft': '<path fill="none" d="M12 19l7-7 3 3-7 7-3-3z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path fill="none" d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Conference & Summit': '<path fill="none" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Festival & Fair': '<path fill="none" d="M5 3v4M3 5h4M19 3v4M21 5h-4M5 19v4M3 19h4M19 19v4M21 19h-4M12 3v4M12 17v4M3 12h4M17 12h4" stroke="white" stroke-width="2" stroke-linecap="round"/>',
  'Fundraiser & Charity': '<path fill="none" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'Other': '<rect fill="none" x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><line x1="16" y1="2" x2="16" y2="6" stroke="white" stroke-width="2.5" stroke-linecap="round"/><line x1="8" y1="2" x2="8" y2="6" stroke="white" stroke-width="2.5" stroke-linecap="round"/><line x1="3" y1="10" x2="21" y2="10" stroke="white" stroke-width="2.5" stroke-linecap="round"/>',
};

export function getEventCategoryIconSvg(category, size = 20) {
  const cat = category || 'Other';
  const icon = EVENT_CATEGORY_ICONS[cat] || EVENT_CATEGORY_ICONS['Other'];
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${icon}</svg>`;
}

export function getEventCategoryColors(evt) {
  const cat = evt.eventCategory || getClientEventCategory(evt);
  return EVENT_CATEGORY_COLORS[cat] || EVENT_CATEGORY_COLORS['Other'];
}

export function getClientEventCategory(evt) {
  const title = (evt.title || evt.name || evt.eventTitle || '').toString();
  const description = (evt.description || '').toString();
  const about = (evt.about || evt.summary || evt.info || '').toString();
  const apiSegment = (evt.apiSegment || '').toString();
  const raw = `${title} ${description} ${about} ${apiSegment}`.toLowerCase();
  const text = raw.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ');
  const tags = (evt.tags || []).map((t) => String(t).toLowerCase());
  const check = (keywords) => keywords.some((kw) => text.includes(kw) || tags.some((t) => t.includes(kw)));
  if (check(['comedy', 'comedian', 'stand up', 'standup', 'improv', 'open mic', 'stand-up'])) return 'Comedy & Entertainment';
  if (check(['film', 'cinema', 'movie', 'screening', 'documentary', 'premiere'])) return 'Film & Cinema';
  if (check(['theater', 'theatre', 'broadway', 'musical', 'ballet', 'performing arts', 'opera'])) return 'Theater & Performing Arts';
  if (check(['gaming', 'esports', 'video game', 'board game', 'card game', 'e-sports'])) return 'Gaming & Esports';
  if (check(['sport', 'basketball', 'football', 'soccer', 'baseball', 'hockey', 'tennis', 'golf', 'marathon', 'fitness', 'yoga', 'gym', 'workout', 'nba', 'nfl', 'mlb', 'nhl', 'mls', 'stadium', 'arena', 'game day', 'playoffs', 'championship', 'mls ', 'run race', 'team game', 'athletic', 'league game'])) return 'Sport & Fitness';
  if (check(['music', 'concert', 'band', 'tour', 'jazz', 'rock', 'pop', 'dj', 'symphony', 'karaoke', 'acoustic', 'live music', 'country music', 'hip hop', 'rap', 'r&b', 'blues', 'electronic', 'edm', 'folk music'])) return 'Music & Concert';
  if (check(['festival', 'fair', 'carnival', 'block party'])) return 'Festival & Fair';
  if (check(['conference', 'summit', 'convention'])) return 'Conference & Summit';
  if (check(['networking', 'meetup', 'mixer', 'professional development', 'career fair'])) return 'Networking & Professional';
  if (check(['business', 'entrepreneur', 'startup', 'career'])) return 'Business & Career';
  if (check(['tech', 'coding', 'developer', 'hackathon', 'programming', 'software', 'data science', 'machine learning', 'ai '])) return 'Tech & Startup';
  if (check(['food', 'wine', 'brewery', 'beer', 'tasting', 'culinary', 'chef', 'dining', 'brunch', 'restaurant', 'food truck', 'coffee'])) return 'Food & Drink';
  if (check(['workshop', 'seminar', 'lecture', 'class', 'course', 'training', 'learning'])) return 'Education & Workshop';
  if (check(['family', 'kids', 'children', 'parent'])) return 'Family & Kids';
  if (check(['volunteer', 'charity', 'community service', 'nonprofit', 'non-profit'])) return 'Volunteering & Community';
  if (check(['outdoor', 'hike', 'hiking', 'camping', 'park', 'nature', 'beach', 'trail', 'kayak', 'bike'])) return 'Outdoor & Adventure';
  if (check(['nightlife', 'party', 'club', 'bar ', 'happy hour', 'night out'])) return 'Nightlife & Party';
  if (check(['wellness', 'mindfulness', 'meditation', 'yoga class', 'retreat'])) return 'Wellness & Mindfulness';
  if (check(['book club', 'reading', 'author talk', 'book signing'])) return 'Book & Reading';
  if (check(['art gallery', 'art museum', 'gallery', 'museum', 'painting', 'photography', 'exhibit', 'arts ', 'culture'])) return 'Arts & Culture';
  if (check(['fundraiser', 'fundraising', 'benefit', 'gala'])) return 'Fundraiser & Charity';
  if (check(['dating', 'singles', 'social mix', 'trivia', 'game night'])) return 'Dating & Social';
  if (check(['hobby', 'craft', 'diy', 'knitting', 'pottery'])) return 'Hobby & Craft';
  return 'Other';
}

export function getHangoutCategory(hangout) {
  const title = (hangout.title || hangout.name || '').toLowerCase();
  const description = (hangout.description || '').toLowerCase();
  const tags = (hangout.tags || []).map(t => String(t).toLowerCase());
  const raw = `${title} ${description} ${tags.join(' ')}`;
  const text = raw.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ');
  const check = (kws) => kws.some(kw => text.includes(kw) || tags.some(t => t.includes(kw)));
  if (check(['sport', 'baseball', 'basketball', 'football', 'soccer', 'tennis', 'golf', 'volleyball', 'swim', 'run', 'marathon', 'gym', 'workout', 'fitness', 'yoga', 'exercise', 'athletic', 'hockey'])) return 'Sport & Fitness';
  if (check(['hike', 'hiking', 'outdoor', 'outdoors', 'camping', 'nature', 'beach', 'trail', 'kayak', 'bike', 'cycling', 'surfing', 'climbing', 'adventure', 'park walk', 'discovery park'])) return 'Outdoor & Adventure';
  if (check(['music', 'concert', 'karaoke', 'jam session', 'band', 'dj', 'singing', 'acoustic', 'live music', 'jazz', 'rap', 'hip hop'])) return 'Music & Concert';
  if (check(['game', 'gaming', 'board game', 'escape room', 'trivia', 'puzzle', 'arcade', 'video game', 'esports', 'lan party'])) return 'Gaming & Esports';
  if (check(['food', 'dinner', 'brunch', 'lunch', 'cooking', 'baking', 'restaurant', 'potluck', 'bbq', 'grill', 'food truck', 'tasting'])) return 'Food & Drink';
  if (check(['coffee', 'tea', 'cafe', 'wine', 'beer', 'brewery', 'bar', 'happy hour', 'cocktail', 'pub', 'drink'])) return 'Food & Drink';
  if (check(['study', 'homework', 'tutoring', 'exam', 'library', 'learning', 'workshop', 'class', 'lecture', 'seminar', 'academic'])) return 'Education & Workshop';
  if (check(['tech', 'coding', 'programming', 'hackathon', 'software', 'data', 'developer'])) return 'Tech & Startup';
  if (check(['art', 'painting', 'drawing', 'sketch', 'gallery', 'museum', 'sculpture', 'craft', 'pottery', 'creative'])) return 'Arts & Culture';
  if (check(['movie', 'film', 'cinema', 'screening', 'watch party', 'netflix'])) return 'Film & Cinema';
  if (check(['book', 'reading', 'book club', 'author', 'literature', 'poetry'])) return 'Book & Reading';
  if (check(['photography', 'photo', 'camera', 'shoot'])) return 'Photography & Creative';
  if (check(['volunteer', 'charity', 'community service', 'nonprofit', 'cleanup'])) return 'Volunteering & Community';
  if (check(['networking', 'meetup', 'mixer', 'professional'])) return 'Networking & Professional';
  if (check(['party', 'nightlife', 'club', 'night out', 'dance', 'rave'])) return 'Nightlife & Party';
  if (check(['wellness', 'meditation', 'mindfulness', 'spa', 'retreat', 'self care'])) return 'Wellness & Mindfulness';
  if (check(['travel', 'trip', 'road trip', 'explore', 'sightseeing', 'tour'])) return 'Travel & Exploration';
  if (check(['comedy', 'comedian', 'standup', 'improv', 'open mic'])) return 'Comedy & Entertainment';
  if (check(['social', 'hangout', 'chill', 'hang out', 'get together'])) return 'Dating & Social';
  return 'Other';
}

/** Map event popup: date + time (uses dateTime from API — not only legacy `date`). */
function formatMapEventDateTimeBlock(evt, tintBg = '#ddd6fe') {
  const raw = evt?.dateTime || evt?.date || evt?.startTime || evt?.localDate || null;
  if (raw == null || raw === '') return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return `<div style="padding: 4px 8px; background: ${tintBg}; border-radius: 6px; margin-bottom: 6px; font-size: 12px; color: #5b21b6; font-weight: 600;">${String(raw)}</div>`;
  }
  const dateLine = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const timeLine = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const whenOneLine = `${dateLine} · ${timeLine}`;
  return `<div style="padding: 6px 8px; background: ${tintBg}; border-radius: 6px; margin-bottom: 6px; font-size: 12px; color: #5b21b6; font-weight: 700; letter-spacing: 0.01em; line-height: 1.35;">${whenOneLine}</div>`;
}

const WorldMapCopy = ({ isOpen, onClose, mapboxToken, user, showMemoryPins = false, allowPinsSwitch = false, defaultLayer = 'all', eventSource = 'huskylink' }) => {
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [groups, setGroups] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState(50); // Default 50km radius
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [routeMode, setRouteMode] = useState('driving'); // driving, walking, cycling, transit
  const [routeData, setRouteData] = useState(null);
  const [etas, setEtas] = useState({});
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const routeLayerRef = useRef(null);
  const lightIntervalRef = useRef(null);
  const [selectedHobbies, setSelectedHobbies] = useState(new Set()); // Track selected hobbies for filtering (clubs + hangouts)
  const [selectedEventIcons, setSelectedEventIcons] = useState(new Set()); // Track selected event icon tags for filtering
  const [selectedHangoutCats, setSelectedHangoutCats] = useState(new Set()); // Track selected hangout categories for filtering
  const [mapDarkMode, setMapDarkMode] = useState(() => {
    return localStorage.getItem('mapDarkMode') === 'true';
  });
  const [mapAutoLight, setMapAutoLight] = useState(() => {
    return localStorage.getItem('mapAutoLight') === 'true';
  });
  const [mapSatelliteMode, setMapSatelliteMode] = useState(() => {
    return localStorage.getItem('mapSatelliteMode') === 'true';
  });
  const [mapLayer, setMapLayer] = useState(defaultLayer); // 'clubs' | 'hangouts' | 'events' | 'all'
  const [hangouts, setHangouts] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsRefreshKey, setEventsRefreshKey] = useState(0);
  const userId = user?.id ?? user?.userId;
  const [eventChat, setEventChat] = useState(null);
  const [eventChatMessages, setEventChatMessages] = useState([]);
  const [eventChatInput, setEventChatInput] = useState('');
  const [eventChatLoading, setEventChatLoading] = useState(false);
  const eventChatEndRef = useRef(null);
  const [memoryPins, setMemoryPins] = useState([]);
  const [notes, setNotes] = useState([]);
  const [memoryFilter, setMemoryFilter] = useState('all');
  const [addMemoryMode, setAddMemoryMode] = useState(false);
  const [addNoteMode, setAddNoteMode] = useState(false);
  const [pendingMemoryPin, setPendingMemoryPin] = useState(null);
  const [pendingNotePin, setPendingNotePin] = useState(null);
  const [pinPixel, setPinPixel] = useState({ x: 0, y: 0 });
  const [newPinType, setNewPinType] = useState('club');
  const [newPinMessage, setNewPinMessage] = useState('');
  const [newPinImage, setNewPinImage] = useState(null);
  const [newNoteType, setNewNoteType] = useState('club');
  const [newNoteText, setNewNoteText] = useState('');
  const memoryFileInputRef = useRef(null);
  const openMemoryPopupRef = useRef(null);

  // When allowPinsSwitch is true, user can toggle between Club Map and Pins (notes/memories) in the bar
  const [pinsMode, setPinsMode] = useState(showMemoryPins);
  const effectiveShowMemoryPins = allowPinsSwitch ? pinsMode : showMemoryPins;

  // Function to get user location
  const getUserLocation = (useGPS = false) => {
    setIsLoadingLocation(true);
    let cancelled = false;
    let resolvedOnce = false;

    const resolveLocation = (coords) => {
      if (cancelled || !coords) return;
      if (!resolvedOnce) {
        resolvedOnce = true;
        setIsLoadingLocation(false);
      }
      setUserLocation(coords);
    };

    // If useGPS is true, prioritize GPS
    if (useGPS && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          resolveLocation([longitude, latitude]);
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Fallback to profile location
          if (user?.location) {
            const { city, state, street, area, pincode } = user.location;
            if (city && state) {
              const addressParts = [];
              if (street) addressParts.push(street);
              if (area) addressParts.push(area);
              if (city) addressParts.push(city);
              if (state) addressParts.push(state);
              if (pincode) addressParts.push(pincode);
              
              const fullAddress = addressParts.join(', ');
              geocodeAddress(fullAddress, mapboxToken)
                .then((coords) => {
                  if (coords) resolveLocation(coords);
                  else resolveLocation([-122.3321, 47.6062]);
                })
                .catch(() => resolveLocation([-122.3321, 47.6062]));
            } else {
              resolveLocation([-122.3321, 47.6062]);
            }
          } else {
            resolveLocation([-122.3321, 47.6062]);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
      return;
    }

    // Try to get location from user profile first
    if (user?.location) {
      const { city, state, street, area, pincode } = user.location;
      if (city && state) {
        // Build full address for geocoding
        const addressParts = [];
        if (street) addressParts.push(street);
        if (area) addressParts.push(area);
        if (city) addressParts.push(city);
        if (state) addressParts.push(state);
        if (pincode) addressParts.push(pincode);
        
        const fullAddress = addressParts.join(', ');
        // Don't block on this — if it resolves faster than GPS, great; otherwise GPS wins.
        geocodeAddress(fullAddress, mapboxToken)
          .then((coords) => {
            if (coords) resolveLocation(coords);
          })
          .catch(() => {});
      }
    }

    // Fallback to browser geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          resolveLocation([longitude, latitude]);
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Default to Seattle if geolocation fails
          resolveLocation([-122.3321, 47.6062]);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 60000
        }
      );
    } else {
      // Default to Seattle if geolocation not available
      resolveLocation([-122.3321, 47.6062]);
    }

    // Hard stop: never show "Locating..." forever
    const hardTimeout = setTimeout(() => {
      if (!resolvedOnce) {
        console.warn('⏱️ Location timeout — using default Seattle');
        resolveLocation([-122.3321, 47.6062]);
      }
    }, 6000);

    return () => {
      cancelled = true;
      clearTimeout(hardTimeout);
    };
  };

  // Get user's current location
  useEffect(() => {
    if (!isOpen) return;
    getUserLocation();
  }, [isOpen, mapboxToken, user]);

  useEffect(() => {
    if (effectiveShowMemoryPins && userId != null) {
      setMemoryPins(loadMemoryPins(userId));
      setNotes(loadNotes(userId));
    }
  }, [effectiveShowMemoryPins, userId]);

  useEffect(() => {
    if (!isLoaded || !map.current || !effectiveShowMemoryPins || (!addMemoryMode && !addNoteMode)) return;
    const handler = (e) => {
      const target = e.originalEvent?.target;
      if (target && target.closest('.custom-marker')) return;
      const coords = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      if (addNoteMode) {
        setPendingNotePin(coords);
        setNewNoteType('club');
        setNewNoteText('');
      } else {
        setPendingMemoryPin(coords);
        setNewPinMessage('');
        setNewPinType('club');
        setNewPinImage(null);
      }
    };
    map.current.on('click', handler);
    return () => { map.current?.off('click', handler); };
  }, [isLoaded, effectiveShowMemoryPins, addMemoryMode, addNoteMode]);

  const activePendingPin = pendingNotePin || pendingMemoryPin;
  useEffect(() => {
    if (!map.current || !activePendingPin) return;
    const update = () => {
      const pt = map.current.project([activePendingPin.lng, activePendingPin.lat]);
      setPinPixel({ x: pt.x, y: pt.y });
    };
    update();
    const m = map.current;
    m.on('move', update);
    m.on('zoom', update);
    return () => { m.off('move', update); m.off('zoom', update); };
  }, [activePendingPin, isLoaded]);

  const pendingPinRef = useRef(activePendingPin);
  useEffect(() => { pendingPinRef.current = activePendingPin; }, [activePendingPin]);
  useEffect(() => {
    if (!isLoaded || !map.current || !effectiveShowMemoryPins) return;
    const closeOnMove = () => {
      if (pendingPinRef.current) {
        setPendingMemoryPin(null);
        setPendingNotePin(null);
      }
      if (openMemoryPopupRef.current) {
        try { openMemoryPopupRef.current.close(); } catch (_) {}
        openMemoryPopupRef.current = null;
      }
    };
    map.current.on('moveend', closeOnMove);
    map.current.on('zoomend', closeOnMove);
    return () => {
      map.current?.off('moveend', closeOnMove);
      map.current?.off('zoomend', closeOnMove);
    };
  }, [isLoaded, effectiveShowMemoryPins]);

  // Fetch groups when component opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchGroups = async () => {
      try {
        // Fetch from major cities (Seattle, etc.) + all=1 for user-created clubs, then merge
        const cities = [
          { city: 'Seattle', state: 'WA' },
          { city: 'Boston', state: 'MA' },
          { city: 'New York', state: 'NY' },
          { city: 'Los Angeles', state: 'CA' },
          { city: 'Chicago', state: 'IL' },
          { city: 'Cambridge', state: 'MA' },
        ];

        const cityGroups = [];
        for (const { city, state } of cities) {
          try {
            const res = await api.get(`/groups?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`);
            if (res.data && Array.isArray(res.data)) cityGroups.push(...res.data);
          } catch (err) {
            console.error(`Error fetching ${city}, ${state}:`, err);
          }
        }

        // Also fetch user-created clubs from any city
        let userCreated = [];
        try {
          const allRes = await api.get('/groups?all=1');
          userCreated = allRes.data && Array.isArray(allRes.data) ? allRes.data : [];
        } catch (err) {
          console.warn('Could not fetch all groups:', err);
        }

        // Merge and dedupe by id
        const seenIds = new Set();
        const allGroups = [];
        [...cityGroups, ...userCreated].forEach((g) => {
          if (g && g.id != null && !seenIds.has(g.id)) {
            seenIds.add(g.id);
            allGroups.push(g);
          }
        });

        console.log(`📊 Fetched ${allGroups.length} groups (${cityGroups.length} from cities + user-created), geocoding...`);

        // Cache geocoding promises so identical addresses only make one API call.
        const geocodeCache = new Map();
        const cachedGeocode = (address) => {
          if (geocodeCache.has(address)) return geocodeCache.get(address);
          const promise = geocodeAddress(address, mapboxToken);
          geocodeCache.set(address, promise);
          return promise;
        };

        const groupsWithCoords = await Promise.all(
          allGroups.map(async (group) => {
            const location = group.location;
            if (!location || !location.city || !location.state) {
              return null;
            }

            let coordinates = null;

            if (location.lat && location.lng) {
              coordinates = [parseFloat(location.lng), parseFloat(location.lat)];
            } else {
              const addressParts = [];
              if (location.street) addressParts.push(location.street);
              if (location.area) addressParts.push(location.area);
              if (location.city) addressParts.push(location.city);
              if (location.state) addressParts.push(location.state);
              if (location.pincode) addressParts.push(location.pincode);

              const fullAddress = addressParts.join(', ');
              coordinates = await cachedGeocode(fullAddress);

              if (!coordinates && location.city && location.state) {
                coordinates = await cachedGeocode(`${location.city}, ${location.state}`);
              }

              if (!coordinates && location.area && location.city && location.state) {
                coordinates = await cachedGeocode(`${location.area}, ${location.city}, ${location.state}`);
              }
            }

            return coordinates ? { ...group, coordinates } : null;
          })
        );
        console.log(`📍 Geocoded with ${geocodeCache.size} unique addresses (saved ${allGroups.length - geocodeCache.size} duplicate API calls)`);

        const validGroups = groupsWithCoords.filter(g => g !== null);
        console.log(`Successfully geocoded ${validGroups.length} out of ${allGroups.length} groups`);
        setGroups(validGroups);
      } catch (error) {
        console.error('Error fetching groups:', error);
        setGroups([]);
      }
    };

    fetchGroups();
  }, [isOpen, mapboxToken]);

  // Fetch hangouts for map
  useEffect(() => {
    if (!isOpen || (mapLayer !== 'hangouts' && mapLayer !== 'all')) return;
    const fetchHangouts = async () => {
      try {
        const res = await api.get('/hangouts');
        const items = res.data || [];
        const withCoords = await Promise.all(items.map(async (h) => {
          const loc = h.location;
          if (!loc || (!loc.city && !loc.state)) return null;
          const addr = [loc.city, loc.state].filter(Boolean).join(', ');
          const coords = await geocodeAddress(addr, mapboxToken);
          return coords ? { ...h, coordinates: coords, _type: 'hangout' } : null;
        }));
        setHangouts(withCoords.filter(Boolean));
      } catch (err) {
        console.error('Error fetching hangouts for map:', err);
        setHangouts([]);
      }
    };
    fetchHangouts();
  }, [isOpen, mapboxToken, mapLayer]);

  // Fetch events for map (HuskyLink or full discover depending on eventSource prop)
  useEffect(() => {
    if (!isOpen || (mapLayer !== 'events' && mapLayer !== 'all')) return;
    const fetchEventsForMap = async () => {
      try {
        let items;
        if (eventSource === 'discover') {
          const loc = user?.location?.city && user?.location?.state
            ? `${user.location.city}, ${user.location.state}` : 'Seattle, WA';
          const res = await api.get('/events/discover', { params: { location: loc, limit: 30 } });
          items = res.data || [];
        } else {
          const [hlRes, neuRes] = await Promise.all([
            api.get('/events/huskylink', { params: { limit: 50 } }),
            api.get('/events/northeastern', { params: { limit: 50 } })
          ]);
          items = [...(hlRes.data || []), ...(neuRes.data || [])];
        }
        const withCoords = await Promise.all(items.map(async (ev) => {
          const eloc = ev.location;
          if (!eloc) return null;
          let coords = null;
          if (eloc.lat && eloc.lng) {
            coords = [parseFloat(eloc.lng), parseFloat(eloc.lat)];
          } else if (eloc.city || eloc.street) {
            const addr = [eloc.street, eloc.city, eloc.state].filter(Boolean).join(', ');
            coords = await geocodeAddress(addr, mapboxToken);
          }
          if (coords && (isNaN(coords[0]) || isNaN(coords[1]))) coords = null;
          return coords ? { ...ev, coordinates: coords, _type: 'event' } : null;
        }));
        setEvents(withCoords.filter(Boolean));
      } catch (err) {
        console.error('Error fetching events for map:', err);
        setEvents([]);
      }
    };
    fetchEventsForMap();
  }, [isOpen, mapboxToken, mapLayer, eventsRefreshKey, user, eventSource]);

  // Fetch event chat messages when event chat is opened
  useEffect(() => {
    if (!eventChat?.chatId) {
      setEventChatMessages([]);
      return;
    }
    let cancelled = false;
    api.get('/messages', { params: { chatId: eventChat.chatId } })
      .then((res) => { if (!cancelled) setEventChatMessages(res.data || []); })
      .catch(() => { if (!cancelled) setEventChatMessages([]); });
    return () => { cancelled = true; };
  }, [eventChat?.chatId]);

  useEffect(() => {
    if (eventChat?.chatId && eventChatMessages.length >= 0) {
      eventChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [eventChat?.chatId, eventChatMessages.length]);

  // Initialize map
  useEffect(() => {
    if (!isOpen || !mapContainer.current || !mapboxToken) return;

    import('mapbox-gl').then((mapboxgl) => {
      mapboxgl.default.accessToken = mapboxToken;

      // Center on user location or default
      const center = userLocation || [-122.3321, 47.6062];

      // When auto light is on: use Standard style (supports light presets). Otherwise: classic styles.
      const styleUrl = mapAutoLight
        ? (mapSatelliteMode ? 'mapbox://styles/mapbox/standard-satellite' : 'mapbox://styles/mapbox/standard')
        : mapSatelliteMode
          ? 'mapbox://styles/mapbox/satellite-streets-v12'
          : mapDarkMode
            ? 'mapbox://styles/mapbox/dark-v11'
            : 'mapbox://styles/mapbox/streets-v12';

      map.current = new mapboxgl.default.Map({
        container: mapContainer.current,
        style: styleUrl,
        center: center,
        zoom: 13,
        attributionControl: false,
        pitch: 50, // 3D tilt for advanced look
        bearing: 0,
        antialias: true,
        projection: 'globe' // Enables globe view + sky/stars when zoomed out
      });

      map.current.addControl(new mapboxgl.default.NavigationControl(), 'top-right');
      map.current.addControl(
        new mapboxgl.default.AttributionControl({ compact: true }),
        'bottom-right'
      );

      const applyLightPreset = (m) => {
        if (!m || !mapAutoLight) return;
        try {
          const preset = getLightPresetFromTime();
          m.setConfigProperty('basemap', 'lightPreset', preset);
        } catch (e) {
          console.warn('Light preset setup:', e);
        }
      };

      const applyFogAndStars = (m) => {
        if (!m || mapAutoLight) return; // Standard style handles lighting; skip custom fog
        try {
          m.setFog({
            'range': [0.5, 10],
            'color': 'rgb(186, 210, 235)',
            'horizon-blend': 0.1,
            'space-color': 'rgb(11, 11, 25)',
            'star-intensity': ['interpolate', ['linear'], ['zoom'], 1, 0.35, 5, 0.6, 10, 0.15, 15, 0]
          });
        } catch (e) {
          console.warn('Fog/sky setup:', e);
        }
      };

      map.current.on('load', () => {
        applyLightPreset(map.current);
        applyFogAndStars(map.current);
        setIsLoaded(true);
      });

      // When auto light is on: update preset every 5 minutes to follow time of day
      if (mapAutoLight) {
        lightIntervalRef.current = setInterval(() => {
          if (map.current) applyLightPreset(map.current);
        }, 5 * 60 * 1000);
      }
    }).catch((error) => {
      console.error('Error loading mapbox-gl:', error);
    });

    return () => {
      if (lightIntervalRef.current) {
        clearInterval(lightIntervalRef.current);
        lightIntervalRef.current = null;
      }
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [isOpen, mapboxToken, userLocation, mapDarkMode, mapSatelliteMode, mapAutoLight]);

  // Listen for satellite map changes from Profile settings
  useEffect(() => {
    const handleMapSatelliteChange = (event) => {
      const { enabled } = event.detail;
      setMapSatelliteMode(enabled);
      if (map.current) {
        const autoLight = localStorage.getItem('mapAutoLight') === 'true';
        const styleUrl = autoLight
          ? (enabled ? 'mapbox://styles/mapbox/standard-satellite' : 'mapbox://styles/mapbox/standard')
          : enabled
            ? 'mapbox://styles/mapbox/satellite-streets-v12'
            : mapDarkMode
              ? 'mapbox://styles/mapbox/dark-v11'
              : 'mapbox://styles/mapbox/streets-v12';
        map.current.setStyle(styleUrl);
        map.current.once('style.load', () => {
          if (autoLight) {
            try {
              map.current.setConfigProperty('basemap', 'lightPreset', getLightPresetFromTime());
            } catch (e) { console.warn('Re-apply light preset:', e); }
          } else {
            try {
              map.current.setFog({
                'range': [0.5, 10],
                'color': 'rgb(186, 210, 235)',
                'horizon-blend': 0.1,
                'space-color': 'rgb(11, 11, 25)',
                'star-intensity': ['interpolate', ['linear'], ['zoom'], 1, 0.35, 5, 0.6, 10, 0.15, 15, 0]
              });
            } catch (e) { console.warn('Re-apply fog:', e); }
          }
        });
      }
    };

    window.addEventListener('mapSatelliteChanged', handleMapSatelliteChange);
    return () => window.removeEventListener('mapSatelliteChanged', handleMapSatelliteChange);
  }, [mapDarkMode]);

  // Listen for automatic light changes from Profile settings
  useEffect(() => {
    const handleMapAutoLightChange = (event) => {
      const { enabled } = event.detail;
      setMapAutoLight(enabled);
      if (map.current) {
        const styleUrl = enabled
          ? (mapSatelliteMode ? 'mapbox://styles/mapbox/standard-satellite' : 'mapbox://styles/mapbox/standard')
          : mapSatelliteMode
            ? 'mapbox://styles/mapbox/satellite-streets-v12'
            : mapDarkMode
              ? 'mapbox://styles/mapbox/dark-v11'
              : 'mapbox://styles/mapbox/streets-v12';
        map.current.setStyle(styleUrl);
        map.current.once('style.load', () => {
          if (enabled) {
            try {
              map.current.setConfigProperty('basemap', 'lightPreset', getLightPresetFromTime());
            } catch (e) { console.warn('Apply light preset:', e); }
            if (lightIntervalRef.current) clearInterval(lightIntervalRef.current);
            lightIntervalRef.current = setInterval(() => {
              if (map.current) {
                try {
                  map.current.setConfigProperty('basemap', 'lightPreset', getLightPresetFromTime());
                } catch (e) {}
              }
            }, 5 * 60 * 1000);
          } else {
            if (lightIntervalRef.current) {
              clearInterval(lightIntervalRef.current);
              lightIntervalRef.current = null;
            }
            try {
              map.current.setFog({
                'range': [0.5, 10],
                'color': 'rgb(186, 210, 235)',
                'horizon-blend': 0.1,
                'space-color': 'rgb(11, 11, 25)',
                'star-intensity': ['interpolate', ['linear'], ['zoom'], 1, 0.35, 5, 0.6, 10, 0.15, 15, 0]
              });
            } catch (e) { console.warn('Re-apply fog:', e); }
          }
          setIsLoaded(true);
        });
      }
    };

    window.addEventListener('mapAutoLightChanged', handleMapAutoLightChange);
    return () => window.removeEventListener('mapAutoLightChanged', handleMapAutoLightChange);
  }, [mapDarkMode, mapSatelliteMode]);

  // Listen for map theme changes from Profile settings
  useEffect(() => {
    const handleMapThemeChange = (event) => {
      const { darkMode } = event.detail;
      setMapDarkMode(darkMode);
      
      if (map.current) {
        const satelliteOn = localStorage.getItem('mapSatelliteMode') === 'true';
        const autoLight = localStorage.getItem('mapAutoLight') === 'true';
        const newStyleUrl = autoLight
          ? (satelliteOn ? 'mapbox://styles/mapbox/standard-satellite' : 'mapbox://styles/mapbox/standard')
          : satelliteOn
            ? 'mapbox://styles/mapbox/satellite-streets-v12'
            : darkMode
              ? 'mapbox://styles/mapbox/dark-v11'
              : 'mapbox://styles/mapbox/streets-v12';
        map.current.setStyle(newStyleUrl);
        map.current.once('style.load', () => {
          if (autoLight) {
            try {
              map.current.setConfigProperty('basemap', 'lightPreset', getLightPresetFromTime());
            } catch (e) { console.warn('Re-apply light preset:', e); }
          } else {
            try {
              map.current.setFog({
                'range': [0.5, 10],
                'color': 'rgb(186, 210, 235)',
                'horizon-blend': 0.1,
                'space-color': 'rgb(11, 11, 25)',
                'star-intensity': ['interpolate', ['linear'], ['zoom'], 1, 0.35, 5, 0.6, 10, 0.15, 15, 0]
              });
            } catch (e) { console.warn('Re-apply fog:', e); }
          }
        });
      }
    };

    window.addEventListener('mapThemeChanged', handleMapThemeChange);
    return () => window.removeEventListener('mapThemeChanged', handleMapThemeChange);
  }, []);

  // Update map center when user location changes
  useEffect(() => {
    if (!isLoaded || !map.current || !userLocation) return;

    map.current.flyTo({
      center: userLocation,
      zoom: 13,
      duration: 1500,
      pitch: 50,
      bearing: 0,
      essential: true
    });
  }, [isLoaded, userLocation]);

  // Add markers and radius circle
  useEffect(() => {
    if (!isLoaded || !map.current || !userLocation) return;

    import('mapbox-gl').then((mapboxgl) => {
      // Clear existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }

      // Remove existing circle layers and source if they exist
      try {
        if (map.current.getLayer('radius-circle-border')) {
          map.current.removeLayer('radius-circle-border');
        }
        if (map.current.getLayer('radius-circle')) {
          map.current.removeLayer('radius-circle');
        }
        if (map.current.getSource('radius-circle')) {
          map.current.removeSource('radius-circle');
        }
      } catch (e) {
        // Source or layer might not exist, ignore error
        console.log('Cleaning up radius circle:', e);
      }

      // Add user location marker with pulsing animation
      const userEl = document.createElement('div');
      userEl.className = 'user-location-marker';
      userEl.style.width = '24px';
      userEl.style.height = '24px';
      userEl.style.borderRadius = '50%';
      userEl.style.backgroundColor = '#3b82f6';
      userEl.style.border = '4px solid white';
      userEl.style.boxShadow = '0 0 0 0 rgba(59, 130, 246, 0.7), 0 2px 12px rgba(0,0,0,0.4)';
      userEl.style.cursor = 'pointer';
      userEl.style.display = 'block';
      userEl.style.zIndex = '1000';
      
      // Add pulsing animation
      const pulse = document.createElement('div');
      pulse.style.position = 'absolute';
      pulse.style.top = '50%';
      pulse.style.left = '50%';
      pulse.style.transform = 'translate(-50%, -50%)';
      pulse.style.width = '24px';
      pulse.style.height = '24px';
      pulse.style.borderRadius = '50%';
      pulse.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
      pulse.style.animation = 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite';
      userEl.appendChild(pulse);
      
      // Add CSS animation if not already added
      if (!document.getElementById('map-pulse-animation')) {
        const style = document.createElement('style');
        style.id = 'map-pulse-animation';
        style.textContent = `
          @keyframes pulse {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            50% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }

      userMarkerRef.current = new mapboxgl.default.Marker({
        element: userEl,
        anchor: 'center'
      })
        .setLngLat(userLocation)
        .setPopup(
          new mapboxgl.default.Popup({ offset: 25 })
            .setHTML(`
              <div style="padding: 8px;">
                <h3 style="margin: 0; font-weight: 600; font-size: 14px;">Your Location</h3>
                <p style="margin: 4px 0 0 0; color: #666; font-size: 12px;">${user?.location?.city || 'Current location'}, ${user?.location?.state || ''}</p>
              </div>
            `)
        )
        .addTo(map.current);

      // Add radius circle
      const radiusInMeters = radius * 1000; // Convert km to meters
      
      // Create circle using Turf.js (included with Mapbox GL)
      const circle = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[]]
        }
      };

      // Generate circle points
      const points = 64;
      for (let i = 0; i <= points; i++) {
        const angle = (i * 360) / points;
        const dx = radiusInMeters * Math.cos(angle * Math.PI / 180);
        const dy = radiusInMeters * Math.sin(angle * Math.PI / 180);
        
        // Convert meters to degrees (approximate)
        const latOffset = dy / 111320;
        const lngOffset = dx / (111320 * Math.cos(userLocation[1] * Math.PI / 180));
        
        circle.geometry.coordinates[0].push([
          userLocation[0] + lngOffset,
          userLocation[1] + latOffset
        ]);
      }

      // Check if source exists, if so update it, otherwise add it
      if (map.current.getSource('radius-circle')) {
        // Source exists, just update the data
        map.current.getSource('radius-circle').setData(circle);
      } else {
        // Source doesn't exist, add it and layers
        map.current.addSource('radius-circle', {
          type: 'geojson',
          data: circle
        });

        // Only add layers if they don't exist
        if (!map.current.getLayer('radius-circle')) {
          map.current.addLayer({
            id: 'radius-circle',
            type: 'fill',
            source: 'radius-circle',
            paint: {
              'fill-color': '#3b82f6',
              'fill-opacity': 0.15,
              'fill-outline-color': '#3b82f6'
            }
          });
        }

        if (!map.current.getLayer('radius-circle-border')) {
          map.current.addLayer({
            id: 'radius-circle-border',
            type: 'line',
            source: 'radius-circle',
            paint: {
              'line-color': '#60a5fa',
              'line-width': 3,
              'line-opacity': 0.8,
              'line-dasharray': [2, 2]
            }
          });
        }
      }

      // Determine which dataset to render based on active layer
      const coordKey = (lng, lat) => `${Number(lng).toFixed(6)},${Number(lat).toFixed(6)}`;
      const sameSpotIndex = new Map();
      const getOffsetLngLat = (lng, lat, index) => {
        if (index === 0) return [lng, lat];
        const rMeters = 18 + index * 8;
        const angle = index * 1.9;
        const dLat = (rMeters / 111_320) * Math.cos(angle);
        const dLng = (rMeters / (111_320 * Math.cos((lat * Math.PI) / 180))) * Math.sin(angle);
        return [lng + dLng, lat + dLat];
      };

      if (!effectiveShowMemoryPins && (mapLayer === 'clubs' || mapLayer === 'all')) {
        // ── CLUBS layer ──
        console.log(`🗺️ Adding markers: ${groups.length} total groups, user location: [${userLocation[0]}, ${userLocation[1]}], radius: ${radius}km`);

        const filteredGroups = groups.filter(group => {
          if (!group.coordinates || !Array.isArray(group.coordinates) || group.coordinates.length !== 2) return false;
          if (selectedHobbies.size > 0) {
            const groupHobbies = Array.isArray(group.hobbies) ? group.hobbies.map(h => h.toLowerCase().trim()) : [];
            const groupTags = Array.isArray(group.tags) ? group.tags.map(t => t.toLowerCase().trim()) : [];
            const allGroupHobbies = [...groupHobbies, ...groupTags];
            const hobbyMatch = Array.from(selectedHobbies).some(selectedHobby => {
              const selectedLower = selectedHobby.toLowerCase().trim();
              return allGroupHobbies.some(gh => gh === selectedLower || gh.includes(selectedLower) || selectedLower.includes(gh));
            });
            if (!hobbyMatch) return false;
          }
          const [groupLng, groupLat] = group.coordinates;
          const [userLng, userLat] = userLocation;
          return calculateDistance(userLat, userLng, groupLat, groupLng) <= radius;
        });

        // Pre-cluster: collapse same-address groups into single representatives
        const _addrMap = new Map();
        filteredGroups.forEach(g => {
          const loc = g.location || {};
          const fp = [loc.street, loc.area, loc.city, loc.state, loc.pincode].filter(Boolean).join('|').toLowerCase();
          if (!_addrMap.has(fp)) _addrMap.set(fp, []);
          _addrMap.get(fp).push(g);
        });
        const displayGroups = [];
        _addrMap.forEach(arr => {
          if (arr.length > 3) {
            displayGroups.push({ ...arr[0], _clusterCount: arr.length, _clusterItems: arr.slice(0, 6) });
          } else {
            displayGroups.push(...arr);
          }
        });

        displayGroups.forEach((group) => {
          if (!group.coordinates || !Array.isArray(group.coordinates) || group.coordinates.length !== 2) return;
          const [lngRaw, latRaw] = group.coordinates;
          if (typeof lngRaw !== 'number' || typeof latRaw !== 'number' || lngRaw < -180 || lngRaw > 180 || latRaw < -90 || latRaw > 90) return;

          const key = coordKey(lngRaw, latRaw);
          const idx = sameSpotIndex.get(key) || 0;
          sameSpotIndex.set(key, idx + 1);
          const [lng, lat] = getOffsetLngLat(lngRaw, latRaw, idx);

          const isCluster = group._clusterCount > 0;
          const markerSize = isCluster ? 56 : 44;

          const el = document.createElement('div');
          el.className = 'custom-marker';
          el.style.width = markerSize + 'px';
          el.style.height = markerSize + 'px';
          el.style.display = 'block';
          el.style.cursor = 'pointer';
          if (isCluster) el.style.position = 'relative';

          const bubble = document.createElement('div');
          bubble.style.width = markerSize + 'px';
          bubble.style.height = markerSize + 'px';
          bubble.style.borderRadius = '50%';
          bubble.style.border = '3px solid rgba(255,255,255,0.95)';
          bubble.style.boxShadow = '0 6px 18px rgba(0,0,0,0.35)';
          bubble.style.overflow = 'hidden';
          bubble.style.transition = 'box-shadow 0.2s ease';

          const img = document.createElement('img');
          img.src = getGroupAvatarSrc(group);
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          img.style.display = 'block';
          img.onerror = () => { img.src = 'https://via.placeholder.com/44'; };

          bubble.appendChild(img);
          el.appendChild(bubble);

          if (isCluster) {
            const badge = document.createElement('div');
            const cnt = group._clusterCount;
            badge.textContent = cnt >= 1000 ? '+' + Math.floor(cnt / 100) * 100 : '+' + cnt;
            badge.style.cssText = 'position:absolute;top:-6px;right:-6px;background:linear-gradient(135deg,#3b82f6,#2563eb);color:#fff;font-size:10px;font-weight:700;padding:2px 6px;border-radius:10px;border:2px solid #fff;box-shadow:0 2px 6px rgba(59,130,246,0.5);line-height:1.2;z-index:2;';
            el.appendChild(badge);
          }

          el.addEventListener('mouseenter', () => { bubble.style.boxShadow = '0 10px 26px rgba(0,0,0,0.45)'; el.style.zIndex = '1000'; });
          el.addEventListener('mouseleave', () => { bubble.style.boxShadow = '0 6px 18px rgba(0,0,0,0.35)'; el.style.zIndex = 'auto'; });

          const distance = calculateDistance(userLocation[1], userLocation[0], latRaw, lngRaw);

          const popupHtml = (() => {
            if (isCluster) {
              const cnt = group._clusterCount;
              const schoolName = group.school || '';
              const location = group.location || {};
              const items = (group._clusterItems || []).map(g => {
                const av = getGroupAvatarSrc(g);
                return '<div class="cluster-item" data-group-id="' + g.id + '" style="display:flex;align-items:center;gap:8px;padding:6px 4px;border-bottom:1px solid #f3f4f6;cursor:pointer;" onmouseover="this.style.background=\'#f0f0f0\'" onmouseout="this.style.background=\'transparent\'">' +
                  '<img src="' + av + '" style="width:30px;height:30px;border-radius:50%;object-fit:cover;flex-shrink:0;" onerror="this.src=\'https://via.placeholder.com/30\'" />' +
                  '<div style="flex:1;min-width:0;"><div style="font-weight:600;font-size:12px;color:#111;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + (g.name || 'Group') + '</div>' +
                  (g.school ? '<div style="font-size:10px;color:#374151;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + g.school + '</div>' : '') +
                  '<div style="font-size:10px;color:#888;">' + (g.category || '') + '</div></div></div>';
              }).join('');
              const cid = 'cl-' + String(group.id);
              return '<div style="padding:12px;min-width:260px;max-width:300px;">' +
                '<div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">' +
                '<div style="width:40px;height:40px;border-radius:50%;background:#3b82f6;display:flex;align-items:center;justify-content:center;flex-shrink:0;"><span style="color:#fff;font-weight:800;font-size:14px;">' + cnt + '</span></div>' +
                '<div><h3 style="margin:0;font-weight:700;font-size:15px;">' + (schoolName || location.city || 'Clubs') + '</h3>' +
                '<span style="color:#888;font-size:12px;">' + cnt + ' clubs here</span></div></div>' +
                '<div style="padding:6px 8px;background:#f3f4f6;border-radius:8px;margin-bottom:8px;font-size:12px;color:#374151;">' +
                (location.street ? '<b>' + location.street + '</b><br/>' : '') +
                (location.area ? location.area + ', ' : '') + (location.city || '') + (location.state ? ', ' + location.state : '') + '</div>' +
                '<div id="' + cid + '" style="max-height:180px;overflow-y:auto;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:8px;">' + items +
                (cnt > 6 ? '<div style="text-align:center;padding:6px;color:#3b82f6;font-size:12px;font-weight:600;">and ' + (cnt - 6) + ' more...</div>' : '') + '</div>' +
                '<div style="color:#3b82f6;font-size:13px;font-weight:600;margin-bottom:8px;">' + distance.toFixed(1) + ' km away</div></div>';
            }
            const g = group;
            const location = g.location || {};
            const gAvatar = getGroupAvatarSrc(g);
            const infoBtnId = 'info-btn-' + (g.id != null ? String(g.id) : 'm-' + lng + '-' + lat).replace(/[^a-zA-Z0-9-_.]/g, '_');
            const directionsBtnId = 'directions-btn-' + (g.id != null ? String(g.id) : 'm-' + lng + '-' + lat).replace(/[^a-zA-Z0-9-_.]/g, '_');
            return '<div style="padding:12px;min-width:240px;background:linear-gradient(135deg,#fff 0%,#f8fafc 100%);border-radius:12px;">' +
              '<div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">' +
              '<img src="' + gAvatar + '" style="width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid #e5e7eb;" />' +
              '<div style="flex:1;min-width:0;"><h3 style="margin:0 0 2px 0;font-weight:700;font-size:15px;color:#111827;">' + (g.name || 'Group') + '</h3>' +
              '<div style="display:inline-flex;align-items:center;gap:6px;">' +
              '<span style="color:#6b7280;font-size:12px;font-weight:500;">' + (g.category || '') + '</span>' +
              '<button id="' + infoBtnId + '" type="button" style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;padding:0;background:#e5e7eb;color:#374151;border:none;border-radius:50%;font-size:11px;font-weight:700;cursor:pointer;line-height:1;">&#8505;</button>' +
              '</div>' +
              (g.school ? '<p style="margin:4px 0 0 0;color:#374151;font-size:12px;font-weight:600;line-height:1.3;">' + g.school + '</p>' : '') +
              '</div></div>' +
              '<div style="padding:8px;background:#f3f4f6;border-radius:8px;margin-bottom:8px;"><p style="margin:0;color:#374151;font-size:12px;line-height:1.5;">' +
              (location.street ? '<strong>' + location.street + '</strong><br/>' : '') +
              (location.area ? location.area + ', ' : '') + (location.city || '') + (location.city && location.state ? ', ' : '') + (location.state || '') +
              (location.pincode ? ' ' + location.pincode : '') + '</p></div>' +
              '<div style="display:flex;justify-content:space-between;align-items:center;padding-top:8px;border-top:1px solid #e5e7eb;">' +
              '<span style="color:#3b82f6;font-size:13px;font-weight:600;">' + distance.toFixed(1) + ' km</span>' +
              (g.members ? '<div style="color:#6b7280;font-size:12px;">&#128101; ' + g.members + ' members</div>' : '') + '</div>' +
              '<button id="' + directionsBtnId + '" style="width:100%;margin-top:8px;padding:8px;background:#3b82f6;color:#fff;border:none;border-radius:6px;font-weight:600;font-size:13px;cursor:pointer;">Directions</button></div>';
          })();

          const marker = new mapboxgl.default.Marker({ element: el, anchor: 'center' })
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.default.Popup({ offset: 25, className: 'custom-popup', maxWidth: isCluster ? '320px' : '280px' }).setHTML(popupHtml))
            .addTo(map.current);

          const safeId = (group.id != null ? String(group.id) : 'm-' + lng + '-' + lat).replace(/[^a-zA-Z0-9-_.]/g, '_');
          marker.getPopup().on('open', () => {
            setTimeout(() => {
              if (isCluster) {
                const cid = 'cl-' + String(group.id);
                document.getElementById(cid)?.querySelectorAll('.cluster-item').forEach(item => {
                  item.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const gId = item.getAttribute('data-group-id');
                    if (gId) { onClose?.(); navigate('/groups/' + gId); }
                  });
                });
              } else {
                document.getElementById('directions-btn-' + safeId)?.addEventListener('click', (e) => {
                  e.stopPropagation();
                  setSelectedDestination({ ...group, coordinates: [lng, lat] });
                });
                document.getElementById('info-btn-' + safeId)?.addEventListener('click', (e) => {
                  e.stopPropagation();
                  onClose?.();
                  navigate('/groups/' + group.id);
                });
              }
            }, 100);
          });

          markersRef.current.push(marker);
        });

      }
      if (!effectiveShowMemoryPins && (mapLayer === 'hangouts' || mapLayer === 'all')) {
        // ── HANGOUTS layer ──
        const filtered = hangouts.filter(h => {
          if (!h.coordinates || h.coordinates.length !== 2) return false;
          const [hLng, hLat] = h.coordinates;
          const [uLng, uLat] = userLocation;
          if (calculateDistance(uLat, uLng, hLat, hLng) > radius) return false;
          if (selectedHangoutCats.size > 0) {
            const cat = getHangoutCategory(h);
            if (!selectedHangoutCats.has(cat)) return false;
          }
          if (selectedHobbies.size > 0) {
            const hTags = [...(Array.isArray(h.tags) ? h.tags : []), h.category || ''].map(t => t.toLowerCase().trim()).filter(Boolean);
            const match = Array.from(selectedHobbies).some(sel => hTags.some(t => t.includes(sel.toLowerCase()) || sel.toLowerCase().includes(t)));
            if (!match) return false;
          }
          return true;
        });

        filtered.forEach((hangout) => {
          const [lngRaw, latRaw] = hangout.coordinates;
          const key = coordKey(lngRaw, latRaw);
          const idx = sameSpotIndex.get(key) || 0;
          sameSpotIndex.set(key, idx + 1);
          const [lng, lat] = getOffsetLngLat(lngRaw, latRaw, idx);

          const hangoutId_ = hangout._id || hangout.id || 0;
          const avatarUrl = hangout.creatorAvatar
            || `https://randomuser.me/api/portraits/${hangoutId_ % 2 === 0 ? 'women' : 'men'}/${(hangoutId_ * 13 + 7) % 80}.jpg`;

          const el = document.createElement('div');
          el.className = 'custom-marker';
          el.style.width = '40px';
          el.style.height = '40px';
          el.style.display = 'block';
          el.style.cursor = 'pointer';

          const bubble = document.createElement('div');
          bubble.style.width = '42px';
          bubble.style.height = '42px';
          bubble.style.borderRadius = '50%';
          bubble.style.border = '3px solid #f59e0b';
          bubble.style.boxShadow = '0 6px 18px rgba(245,158,11,0.4)';
          bubble.style.display = 'flex';
          bubble.style.alignItems = 'center';
          bubble.style.justifyContent = 'center';
          bubble.style.transition = 'box-shadow 0.2s ease';
          bubble.style.overflow = 'hidden';
          bubble.style.background = '#fbbf24';
          const img = document.createElement('img');
          img.src = avatarUrl;
          img.alt = hangout.creatorName || '';
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          img.style.borderRadius = '50%';
          bubble.appendChild(img);
          el.appendChild(bubble);

          el.addEventListener('mouseenter', () => { bubble.style.boxShadow = '0 10px 26px rgba(245,158,11,0.55)'; el.style.zIndex = '1000'; });
          el.addEventListener('mouseleave', () => { bubble.style.boxShadow = '0 6px 18px rgba(245,158,11,0.4)'; el.style.zIndex = 'auto'; });

          const distance = calculateDistance(userLocation[1], userLocation[0], latRaw, lngRaw);
          const loc = hangout.location || {};
          const hangoutId = hangout._id || hangout.id;
          const dateStr = hangout.date ? new Date(hangout.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
          const creatorAvatarHtml = `<img src="${avatarUrl}" alt="${hangout.creatorName || ''}" style="width: 36px; height: 36px; border-radius: 50%; object-fit: cover; border: 2px solid #f59e0b;" />`;
          const popupHtml = `
            <div style="padding: 12px; min-width: 220px; background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.15);">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                ${creatorAvatarHtml}
                <div style="flex: 1;">
                  <h3 style="margin: 0 0 2px 0; font-weight: 700; font-size: 14px; color: #92400e;">${hangout.title || hangout.name || 'Hangout'}</h3>
                  <p style="margin: 0; color: #b45309; font-size: 11px; font-weight: 500;">${hangout.creatorName ? `by ${hangout.creatorName}` : (hangout.category || 'Hangout')}</p>
                  <p style="margin: 2px 0 0 0; color: #78350f; font-size: 11px; font-weight: 600; line-height: 1.3;">${hangout.school || ''}</p>
                </div>
              </div>
              ${dateStr ? `<div style="padding: 4px 8px; background: #fde68a; border-radius: 6px; margin-bottom: 6px; font-size: 12px; color: #92400e; font-weight: 600;">${dateStr}</div>` : ''}
              <div style="padding: 6px 8px; background: rgba(255,255,255,0.7); border-radius: 8px; margin-bottom: 6px;">
                <p style="margin: 0; color: #374151; font-size: 12px;">${loc.city || ''}${loc.city && loc.state ? ', ' : ''}${loc.state || ''}</p>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                <span style="color: #d97706; font-size: 13px; font-weight: 600;">${distance.toFixed(1)} km</span>
                ${hangout.spotsLeft != null ? `<span style="color: #92400e; font-size: 12px;">${hangout.spotsLeft} spots left</span>` : ''}
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
                <button id="add-to-calendar-hangout-${hangoutId}" type="button" title="Add to calendar" style="background: none; border: none; cursor: pointer; padding: 0 2px; display: inline-flex; align-items: center; justify-content: center; vertical-align: middle;" aria-label="Add to calendar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                    <rect fill="none" x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </button>
                <button
                  id="hangout-chat-btn-${hangoutId}"
                  type="button"
                  title="Chat with others in this hangout"
                  style="background: linear-gradient(135deg,#fbbf24,#f59e0b); border: none; cursor: pointer; padding: 6px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 6px; box-shadow: 0 2px 8px rgba(245,158,11,0.45);"
                  aria-label="Hangout chat"
                >
                  <span style="display:inline-flex; width:18px; height:18px;">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path fill="none" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle fill="none" cx="9" cy="7" r="4"/>
                      <path fill="none" d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path fill="none" d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </span>
                  <span style="color:white; font-size:12px; font-weight:600;">Chat</span>
                </button>
              </div>
              <div id="add-to-calendar-confirm-hangout-${hangoutId}" style="display: none; margin-top: 8px; padding: 10px 12px; background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); border: 1px solid #fed7aa; border-radius: 10px; font-size: 13px; color: #92400e; font-weight: 600;">
                <p style="margin: 0 0 8px 0;">Add this hangout to your calendar?</p>
                <div style="display: flex; gap: 6px;">
                  <button id="add-to-calendar-hangout-cancel-${hangoutId}" type="button" style="flex: 1; padding: 6px 12px; background: rgba(255,255,255,0.9); color: #92400e; border: 1px solid #fed7aa; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;">Cancel</button>
                  <button id="add-to-calendar-hangout-ok-${hangoutId}" type="button" style="flex: 1; padding: 6px 12px; background: #f97316; color: white; border: none; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; box-shadow: 0 2px 8px rgba(249,115,22,0.5);">Add</button>
                </div>
              </div>
              <button id="directions-btn-hangout-${hangoutId}" style="width: 100%; margin-top: 8px; padding: 8px; background: #f59e0b; color: white; border: none; border-radius: 6px; font-weight: 600; font-size: 13px; cursor: pointer; transition: background 0.2s;" onmouseover="this.style.background='#d97706'" onmouseout="this.style.background='#f59e0b'">
                Directions
              </button>
            </div>
          `;

          const marker = new mapboxgl.default.Marker({ element: el, anchor: 'center' })
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.default.Popup({ offset: 25, className: 'custom-popup', maxWidth: '260px' }).setHTML(popupHtml))
            .addTo(map.current);

          marker.getPopup().on('open', () => {
            setTimeout(() => {
              const directionsBtn = document.getElementById(`directions-btn-hangout-${hangoutId}`);
              if (directionsBtn) {
                directionsBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  setSelectedDestination({ name: hangout.title || hangout.name || 'Hangout', location: hangout.location, coordinates: [lng, lat] });
                });
              }
              const chatBtn = document.getElementById(`hangout-chat-btn-${hangoutId}`);
              if (chatBtn) {
                chatBtn.addEventListener('click', async (e) => {
                  e.stopPropagation();
                  try {
                    // Treat opening chat as implicitly joining the hangout so it shows in Upcoming Hangouts / Hangout chats
                    if (hangoutId != null) {
                      await api.post(`/hangouts/${hangoutId}/join`);
                    }
                  } catch (err) {
                    // Ignore join errors (already joined, etc.) and still open chat
                    console.warn('Join hangout failed (chat will still open):', err?.message || err);
                  }
                  navigate('/friends', {
                    state: {
                      openHangoutChat: {
                        chatId: `hangout-${hangoutId}`,
                        title: hangout.title || hangout.name || 'Hangout'
                      }
                    }
                  });
                });
              }
              const addCalBtn = document.getElementById(`add-to-calendar-hangout-${hangoutId}`);
              const confirmEl = document.getElementById(`add-to-calendar-confirm-hangout-${hangoutId}`);
              const cancelBtn = document.getElementById(`add-to-calendar-hangout-cancel-${hangoutId}`);
              const okBtn = document.getElementById(`add-to-calendar-hangout-ok-${hangoutId}`);
              const doAddHangoutToCalendar = async () => {
                const title = hangout.title || hangout.name || 'Hangout';
                const startDate = hangout.date ? new Date(hangout.date) : new Date();
                try {
                  await api.post('/events/add-to-calendar', {
                    title,
                    date: startDate.toISOString(),
                    location: hangout.location || {},
                    url: '',
                    source: 'hangout'
                  });
                } catch (err) {
                  console.warn('Could not save to calendar:', err);
                }
                if (confirmEl) {
                  confirmEl.innerHTML = '<p style="margin:0;color:#059669;font-size:13px;font-weight:600;">✓ Added to your calendar!</p>';
                  setTimeout(() => { if (confirmEl) confirmEl.style.display = 'none'; }, 2000);
                }
              };
              if (addCalBtn && confirmEl) {
                addCalBtn.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmEl.style.display = 'block';
                });
              }
              if (cancelBtn && confirmEl) {
                cancelBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  confirmEl.style.display = 'none';
                });
              }
              if (okBtn && confirmEl) {
                okBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  doAddHangoutToCalendar();
                });
              }
            }, 100);
          });

          markersRef.current.push(marker);
        });

      }
      if (!effectiveShowMemoryPins && (mapLayer === 'events' || mapLayer === 'all')) {
        // ── EVENTS layer ──
        const filtered = events.filter(ev => {
          if (!ev.coordinates || ev.coordinates.length !== 2) return false;
          const [eLng, eLat] = ev.coordinates;
          const [uLng, uLat] = userLocation;
          if (calculateDistance(uLat, uLng, eLat, eLng) > radius) return false;
          if (selectedEventIcons.size > 0) {
            const iconName = ev.tablerIconName || getEventTablerIconName(ev);
            if (!selectedEventIcons.has(iconName)) return false;
          }
          if (selectedHobbies.size > 0) {
            const eTags = [...(Array.isArray(ev.tags) ? ev.tags : []), ev.category || ''].map(t => t.toLowerCase().trim()).filter(Boolean);
            const match = Array.from(selectedHobbies).some(sel => eTags.some(t => t.includes(sel.toLowerCase()) || sel.toLowerCase().includes(t)));
            if (!match) return false;
          }
          return true;
        });

        filtered.forEach((evt) => {
          const [lngRaw, latRaw] = evt.coordinates;
          const key = coordKey(lngRaw, latRaw);
          const idx = sameSpotIndex.get(key) || 0;
          sameSpotIndex.set(key, idx + 1);
          const [lng, lat] = getOffsetLngLat(lngRaw, latRaw, idx);

          const colors = getEventCategoryColors(evt);
          const displayCategory = evt.eventCategory || getClientEventCategory(evt);

          const el = document.createElement('div');
          el.className = 'custom-marker';
          el.style.width = '40px';
          el.style.height = '40px';
          el.style.display = 'block';
          el.style.cursor = 'pointer';

          const bubble = document.createElement('div');
          bubble.style.width = '40px';
          bubble.style.height = '40px';
          bubble.style.borderRadius = '12px';
          bubble.style.border = `3px solid ${colors.border}`;
          bubble.style.background = `linear-gradient(135deg, ${colors.light}, ${colors.bg})`;
          bubble.style.boxShadow = `0 6px 18px ${colors.shadow}`;
          bubble.style.display = 'flex';
          bubble.style.alignItems = 'center';
          bubble.style.justifyContent = 'center';
          bubble.style.transition = 'box-shadow 0.2s ease';
          const iconName = evt.tablerIconName || getEventTablerIconName(evt);
          bubble.innerHTML = getTablerIconSvgByName(iconName) || getEventCategoryIconSvgTabler(displayCategory);
          el.appendChild(bubble);

          el.addEventListener('mouseenter', () => { bubble.style.boxShadow = `0 10px 26px ${colors.shadow}`; el.style.zIndex = '1000'; });
          el.addEventListener('mouseleave', () => { bubble.style.boxShadow = `0 6px 18px ${colors.shadow}`; el.style.zIndex = 'auto'; });

          const distance = calculateDistance(userLocation[1], userLocation[0], latRaw, lngRaw);
          const loc = evt.location || {};
          const eventTint = `${colors.light}33`;
          const eventWhenHtml = formatMapEventDateTimeBlock(evt, eventTint);
          const sourceLabel = evt.source === 'ticketmaster' ? 'Ticketmaster' : evt.source === 'seatgeek' ? 'SeatGeek' : evt.source === 'meetup' ? 'Meetup' : evt.source || '';
          const evtId = evt.id || Math.random().toString(36).slice(2);
          const popupHtml = `
            <div style="padding: 12px; min-width: 220px; background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%); border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.15); border-top: 4px solid ${colors.bg};">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <div style="width: 36px; height: 36px; border-radius: 10px; background: ${colors.bg}; display: flex; align-items: center; justify-content: center;">
                  ${getTablerIconSvgByName(evt.tablerIconName || getEventTablerIconName(evt), 18) || getEventCategoryIconSvgTabler(displayCategory, 18)}
                </div>
                <div style="flex: 1;">
                  <h3 style="margin: 0 0 2px 0; font-weight: 700; font-size: 14px; color: #4c1d95;">${evt.name || evt.title || 'Event'}</h3>
                  ${displayCategory ? `<p style="margin: 0; color: ${colors.border}; font-size: 11px; font-weight: 600;">${displayCategory}</p>` : ''}
                  ${sourceLabel ? `<span style="display: inline-block; padding: 1px 6px; background: #ddd6fe; color: #6d28d9; border-radius: 4px; font-size: 10px; font-weight: 600;">${sourceLabel}</span>` : ''}
                </div>
              </div>
              ${eventWhenHtml}
              ${evt.image ? `<img src="${evt.image}" style="width: 100%; height: 80px; object-fit: cover; border-radius: 8px; margin-bottom: 6px;" />` : ''}
              <div style="padding: 6px 8px; background: rgba(255,255,255,0.7); border-radius: 8px; margin-bottom: 6px;">
                <p style="margin: 0; color: #374151; font-size: 12px;">${loc.venue ? `${loc.venue}, ` : ''}${loc.city || ''}${loc.city && loc.state ? ', ' : ''}${loc.state || ''}</p>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="color: #7c3aed; font-size: 13px; font-weight: 600;">${distance.toFixed(1)} km</span>
                <span style="display: inline-flex; align-items: center; gap: 6px;">
                  <button id="add-to-calendar-event-${evtId}" type="button" title="Add to calendar" style="background: none; border: none; cursor: pointer; padding: 0 2px; display: inline-flex; align-items: center; justify-content: center; vertical-align: middle;" aria-label="Add to calendar"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;"><rect fill="none" x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></button>
                  ${evt.url ? `<a href="${evt.url}" target="_blank" rel="noopener" style="color: #7c3aed; font-size: 12px; text-decoration: underline; font-weight: 500;">View</a>` : ''}
                  <button id="event-chat-btn-${evtId}" type="button" title="Chat with others going to this event" style="background: none; border: none; cursor: pointer; padding: 0 2px; font-size: 16px; line-height: 1; display: inline-flex; align-items: center; gap: 2px;" aria-label="Event chat"><span style="color: #7c3aed; font-size: 12px; font-weight: 500;">Chat</span></button>
                </span>
              </div>
              <div id="add-to-calendar-confirm-${evtId}" style="display: none; margin-top: 8px; padding: 10px 12px; background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%); border: 1px solid #ddd6fe; border-radius: 10px; font-size: 13px; color: #4c1d95; font-weight: 600;">
                <p style="margin: 0 0 8px 0;">Add to calendar?</p>
                <div style="display: flex; gap: 6px;">
                  <button id="add-to-calendar-cancel-${evtId}" type="button" style="padding: 6px 12px; background: rgba(255,255,255,0.9); color: #5b21b6; border: 1px solid #c4b5fd; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;">Cancel</button>
                  <button id="add-to-calendar-ok-${evtId}" type="button" style="padding: 6px 12px; background: #8b5cf6; color: white; border: none; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; box-shadow: 0 2px 8px rgba(139, 92, 246, 0.35);">Add</button>
                </div>
              </div>
              <button id="directions-btn-event-${evtId}" style="width: 100%; margin-top: 8px; padding: 8px; background: ${colors.bg}; color: white; border: none; border-radius: 6px; font-weight: 600; font-size: 13px; cursor: pointer; transition: background 0.2s;" onmouseover="this.style.background='${colors.border}'" onmouseout="this.style.background='${colors.bg}'">
                Directions
              </button>
            </div>
          `;

          const marker = new mapboxgl.default.Marker({ element: el, anchor: 'center' })
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.default.Popup({ offset: 25, className: 'custom-popup', maxWidth: '280px' }).setHTML(popupHtml))
            .addTo(map.current);

          marker.getPopup().on('open', () => {
            setTimeout(() => {
              const btn = document.getElementById(`directions-btn-event-${evtId}`);
              if (btn) {
                btn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  setSelectedDestination({ name: evt.name || evt.title || 'Event', location: evt.location, coordinates: [lng, lat] });
                });
              }
              const addCalBtn = document.getElementById(`add-to-calendar-event-${evtId}`);
              const confirmEl = document.getElementById(`add-to-calendar-confirm-${evtId}`);
              const cancelBtn = document.getElementById(`add-to-calendar-cancel-${evtId}`);
              const okBtn = document.getElementById(`add-to-calendar-ok-${evtId}`);
              const doAddToCalendar = async () => {
                const title = evt.name || evt.title || 'Event';
                const startDate = (evt.dateTime || evt.date) ? new Date(evt.dateTime || evt.date) : new Date();
                try {
                  await api.post('/events/add-to-calendar', {
                    title,
                    date: startDate.toISOString(),
                    location: evt.location || {},
                    url: evt.url || '',
                    source: evt.source || ''
                  });
                  if (confirmEl) {
                    confirmEl.innerHTML = '<p style="margin:0;color:#059669;font-size:13px;font-weight:600;">✓ Added to your calendar!</p>';
                    setTimeout(() => { if (confirmEl) confirmEl.style.display = 'none'; }, 2000);
                  }
                } catch (err) {
                  console.warn('Could not save to calendar:', err);
                  if (confirmEl) {
                    confirmEl.innerHTML = '<p style="margin:0;color:#b91c1c;font-size:13px;font-weight:600;">Could not save. Try signing in again.</p>';
                    setTimeout(() => { if (confirmEl) confirmEl.style.display = 'none'; }, 3000);
                  }
                }
              };
              if (addCalBtn && confirmEl) {
                addCalBtn.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmEl.style.display = 'block';
                });
              }
              if (cancelBtn && confirmEl) {
                cancelBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  confirmEl.style.display = 'none';
                });
              }
              if (okBtn && confirmEl) {
                okBtn.addEventListener('click', async (e) => {
                  e.stopPropagation();
                  await doAddToCalendar();
                });
              }
              const chatBtn = document.getElementById(`event-chat-btn-${evtId}`);
              if (chatBtn) {
                chatBtn.addEventListener('click', async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const res = await api.post('/event-chats', {
                      eventId: evt.id,
                      source: evt.source,
                      title: evt.title,
                      name: evt.name,
                      dateTime: evt.dateTime,
                      endTime: evt.endTime,
                      eventCategory: evt.eventCategory || getClientEventCategory(evt),
                    });
                    navigate('/friends', {
                      state: {
                        openEventChat: {
                          chatId: res.data.chatId,
                          eventTitle: res.data.eventTitle || evt.title || evt.name || 'Event'
                        }
                      }
                    });
                  } catch (err) {
                    console.warn('Event chat open failed', err);
                  }
                });
              }
            }, 100);
          });

          markersRef.current.push(marker);
        });
      }

      if (effectiveShowMemoryPins && !addNoteMode && Array.isArray(memoryPins) && memoryPins.length > 0) {
        const filteredPins = memoryFilter === 'all' ? memoryPins : memoryPins.filter(p => p.type === memoryFilter);
        filteredPins.forEach((pin) => {
          const cfg = memoryTypeConfig[pin.type] || memoryTypeConfig.club;
          const el = document.createElement('div');
          el.className = 'custom-marker';
          el.style.width = '44px';
          el.style.height = '44px';
          el.style.display = 'block';
          el.style.cursor = 'pointer';
          const bubble = document.createElement('div');
          bubble.style.width = '44px';
          bubble.style.height = '44px';
          bubble.style.borderRadius = '50%';
          bubble.style.border = `3px solid ${cfg.color}`;
          bubble.style.boxShadow = '0 6px 18px rgba(0,0,0,0.35)';
          bubble.style.overflow = 'hidden';
          bubble.style.transition = 'box-shadow 0.2s ease';
          if (pin.imageData) {
            const img = document.createElement('img');
            img.src = pin.imageData;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
            img.style.display = 'block';
            bubble.appendChild(img);
          } else {
            bubble.style.background = `linear-gradient(135deg, ${cfg.color}, ${cfg.color}dd)`;
            bubble.style.display = 'flex';
            bubble.style.alignItems = 'center';
            bubble.style.justifyContent = 'center';
            const letter = document.createElement('span');
            letter.textContent = cfg.label.charAt(0);
            letter.style.color = 'white';
            letter.style.fontSize = '18px';
            letter.style.fontWeight = '800';
            letter.style.lineHeight = '1';
            bubble.appendChild(letter);
          }
          el.appendChild(bubble);
          el.addEventListener('mouseenter', () => { bubble.style.boxShadow = '0 10px 26px rgba(0,0,0,0.45)'; el.style.zIndex = '1000'; });
          el.addEventListener('mouseleave', () => { bubble.style.boxShadow = '0 6px 18px rgba(0,0,0,0.35)'; el.style.zIndex = 'auto'; });
          const imgHtml = pin.imageData ? `<img src="${pin.imageData.replace(/"/g, '&quot;')}" style="width: 100%; max-width: 200px; height: 120px; object-fit: cover; border-radius: 8px; margin-bottom: 8px;" alt="" />` : '';
          const pinId = `memory-${pin.lat}-${pin.lng}-${(pin.type || 'club').replace(/[^a-zA-Z0-9]/g, '')}`;
          const popupHtml = `
            <div style="padding: 12px; min-width: 200px;">
              ${imgHtml}
              <strong style="font-size: 13px; color: #111;">${cfg.label}</strong>
              <p style="margin: 6px 0 0 0; font-size: 12px; color: #444;">${(pin.message || 'No description').replace(/</g, '&lt;').replace(/\n/g, '<br/>')}</p>
              <a id="google-photos-${pinId}" href="https://photos.google.com/" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; margin-top: 10px; padding: 8px; background: #f8fafc; color: #1a73e8; border: 1px solid #dadce0; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; text-decoration: none; transition: background 0.2s, border-color 0.2s;" onmouseover="this.style.background='#e8f0fe'; this.style.borderColor='#1a73e8';" onmouseout="this.style.background='#f8fafc'; this.style.borderColor='#dadce0';">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect fill="none" x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle fill="none" cx="8.5" cy="8.5" r="1.5"/><path fill="none" d="M21 15l-5-5L5 21"/></svg>
                Add to Google Photos
              </a>
            </div>`;
          const popup = new mapboxgl.default.Popup({ offset: 25, className: 'custom-popup' }).setHTML(popupHtml);
          popup.on('open', () => {
            openMemoryPopupRef.current = popup;
            setPendingMemoryPin(null);
            setPendingNotePin(null);
            setTimeout(() => {
              const btn = document.getElementById(`google-photos-${pinId}`);
              if (btn) {
                btn.addEventListener('click', async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const caption = `Saved from ClubFinder: ${cfg.label} memory${pin.message ? ' — ' + pin.message.replace(/\n/g, ' ').slice(0, 80) : ''}`;
                  const imageSrc = pin.imageData || '';
                  const filename = `ClubFinder-${(cfg.label || 'memory').replace(/[^a-zA-Z0-9-_\s]/g, '')}-memory`;
                  await downloadImageAndOpenGooglePhotos(imageSrc, filename, caption);
                });
              }
            }, 100);
          });
          popup.on('close', () => { if (openMemoryPopupRef.current === popup) openMemoryPopupRef.current = null; });
          const marker = new mapboxgl.default.Marker({ element: el, anchor: 'center' })
            .setLngLat([pin.lng, pin.lat])
            .setPopup(popup)
            .addTo(map.current);
          markersRef.current.push(marker);
        });
      }

      if (effectiveShowMemoryPins && addNoteMode && Array.isArray(notes) && notes.length > 0) {
        const filteredNotes = memoryFilter === 'all' ? notes : notes.filter(n => n.type === memoryFilter);
        filteredNotes.forEach((note) => {
          const cfg = memoryTypeConfig[note.type] || memoryTypeConfig.club;
          const el = document.createElement('div');
          el.className = 'custom-marker';
          el.style.width = '36px';
          el.style.height = '36px';
          el.style.display = 'block';
          el.style.cursor = 'pointer';
          const bubble = document.createElement('div');
          bubble.style.width = '36px';
          bubble.style.height = '36px';
          bubble.style.borderRadius = '50%';
          bubble.style.background = `linear-gradient(135deg, ${cfg.color}, ${cfg.color}dd)`;
          bubble.style.border = '2px solid rgba(255,255,255,0.95)';
          bubble.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
          bubble.style.display = 'flex';
          bubble.style.alignItems = 'center';
          bubble.style.justifyContent = 'center';
          const icon = document.createElement('span');
          icon.textContent = '💭';
          icon.style.fontSize = '16px';
          bubble.appendChild(icon);
          el.appendChild(bubble);
          const noteId = `note-${note.lat}-${note.lng}-${(note.type || 'club').replace(/[^a-zA-Z0-9]/g, '')}`;
          const noteCfg = memoryTypeConfig[note.type] || memoryTypeConfig.club;
          const popupHtml = `
            <div style="padding: 12px; min-width: 180px;">
              <strong style="font-size: 12px; color: #111;">Note</strong>
              <p style="margin: 6px 0 0 0; font-size: 12px; color: #444;">${(note.text || 'No note').replace(/</g, '&lt;').replace(/\n/g, '<br/>')}</p>
              <a id="google-photos-${noteId}" href="https://photos.google.com/" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; margin-top: 10px; padding: 8px; background: #f8fafc; color: #1a73e8; border: 1px solid #dadce0; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; text-decoration: none;" onmouseover="this.style.background='#e8f0fe'; this.style.borderColor='#1a73e8';" onmouseout="this.style.background='#f8fafc'; this.style.borderColor='#dadce0';">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect fill="none" x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle fill="none" cx="8.5" cy="8.5" r="1.5"/><path fill="none" d="M21 15l-5-5L5 21"/></svg>
                Add to Google Photos
              </a>
            </div>`;
          const popup = new mapboxgl.default.Popup({ offset: 25, className: 'custom-popup' }).setHTML(popupHtml);
          popup.on('open', () => {
            setPendingNotePin(null);
            setTimeout(() => {
              const btn = document.getElementById(`google-photos-${noteId}`);
              if (btn) {
                btn.addEventListener('click', async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const caption = `Saved from ClubFinder: ${noteCfg.label} note${note.text ? ' — ' + note.text.replace(/\n/g, ' ').slice(0, 80) : ''}`;
                  await downloadImageAndOpenGooglePhotos('', '', caption);
                });
              }
            }, 100);
          });
          const marker = new mapboxgl.default.Marker({ element: el, anchor: 'center' })
            .setLngLat([note.lng, note.lat])
            .setPopup(popup)
            .addTo(map.current);
          markersRef.current.push(marker);
        });
      }

      console.log(`🎯 Successfully added ${markersRef.current.length} markers to the map (layer: ${mapLayer})`);
    });
  }, [isLoaded, groups, hangouts, events, userLocation, radius, user, selectedHobbies, selectedEventIcons, selectedHangoutCats, mapLayer, effectiveShowMemoryPins, memoryPins, notes, memoryFilter, addNoteMode, addMemoryMode]);

  // Fetch directions and ETAs when destination is selected
  useEffect(() => {
    if (!isLoaded || !map.current || !selectedDestination || !userLocation || !mapboxToken) return;

    const loadDirections = async () => {
      setIsLoadingRoute(true);
      
      // Map mode to Mapbox profile
      const profileMap = {
        driving: 'driving',
        walking: 'walking',
        cycling: 'cycling',
        transit: 'driving' // Transit uses driving profile with transit options
      };
      
      const profile = profileMap[routeMode] || 'driving';
      
      // Fetch route for selected mode
      const route = await fetchDirections(userLocation, selectedDestination.coordinates, profile, mapboxToken);
      if (route) {
        setRouteData(route);
        drawRoute(route);
      }
      
      // Fetch ETAs for all modes (including transit as driving estimate)
      const modes = ['driving', 'walking', 'cycling'];
      const etaPromises = modes.map(async (mode) => {
        const r = await fetchDirections(userLocation, selectedDestination.coordinates, mode, mapboxToken);
        return r ? { mode, duration: r.duration, distance: r.distance } : null;
      });
      
      const results = await Promise.all(etaPromises);
      const etaObj = {};
      results.forEach(r => {
        if (r) {
          const minutes = Math.round(r.duration / 60);
          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;
          const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          etaObj[r.mode] = { time: timeStr, duration: r.duration, distance: (r.distance / 1000).toFixed(1) };
        }
      });
      
      // For transit, use driving time as estimate (transit would require additional API)
      const transitRoute = await fetchDirections(userLocation, selectedDestination.coordinates, 'driving', mapboxToken);
      if (transitRoute) {
        // Estimate transit time as 1.5x driving time (rough estimate)
        const transitDuration = transitRoute.duration * 1.5;
        const minutes = Math.round(transitDuration / 60);
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        etaObj['transit'] = { time: timeStr, duration: transitDuration, distance: (transitRoute.distance / 1000).toFixed(1) };
      }
      
      setEtas(etaObj);
      setIsLoadingRoute(false);
    };

    loadDirections();
  }, [isLoaded, selectedDestination, routeMode, userLocation, mapboxToken]);

  // Draw route on map
  const drawRoute = (route) => {
    if (!map.current || !route) return;

    import('mapbox-gl').then((mapboxgl) => {
      const sourceId = 'route';
      const layerId = 'route-layer';

      // Remove existing route if any
      if (map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
      if (map.current.getSource(sourceId)) {
        map.current.removeSource(sourceId);
      }

      // Add route source
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: route.geometry
        }
      });

      // Add route layer with single color for all modes
      map.current.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 4,
          'line-opacity': 0.8
        }
      });

      // Fit map to route bounds
      const coordinates = route.geometry.coordinates;
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.default.LngLatBounds(coordinates[0], coordinates[0]));

      map.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 400 },
        duration: 1000
      });
    });
  };

  // Recenter map to show route
  const recenterMap = () => {
    if (!map.current || !routeData || !userLocation || !selectedDestination) return;
    
    import('mapbox-gl').then((mapboxgl) => {
      const coordinates = routeData.geometry.coordinates;
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.default.LngLatBounds(coordinates[0], coordinates[0]));

      map.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 400 },
        duration: 1000
      });
    });
  };

  // Clear route when destination is cleared
  const clearRoute = () => {
    if (!map.current) return;
    
    try {
      if (map.current.getLayer('route-layer')) {
        map.current.removeLayer('route-layer');
      }
      if (map.current.getSource('route')) {
        map.current.removeSource('route');
      }
    } catch (e) {
      console.log('Error clearing route:', e);
    }
    
    setSelectedDestination(null);
    setRouteData(null);
    setEtas({});
  };

  // Get unique hobbies from groups with emoji mapping
  const getHobbyEmoji = (hobby) => {
    const hobbyLower = hobby.toLowerCase();
    const emojiMap = {
      'soccer': '⚽', 'football': '⚽',
      'basketball': '🏀',
      'tennis': '🎾',
      'volleyball': '🏐',
      'cycling': '🚴', 'bike': '🚴', 'bicycle': '🚴',
      'running': '🏃', 'fitness': '🏃', 'gym': '🏃',
      'hiking': '⛰️', 'mountain': '⛰️',
      'food': '🍴', 'cooking': '🍴', 'restaurant': '🍴',
      'coffee': '☕',
      'wine': '🍷', 'drink': '🍷',
      'art': '🎨', 'painting': '🎨', 'drawing': '🎨',
      'music': '🎵', 'singing': '🎵',
      'photography': '📷', 'camera': '📷',
      'gaming': '🎮', 'game': '🎮', 'video games': '🎮',
      'reading': '📚', 'book': '📚',
      'university': '🎓', 'college': '🎓', 'school': '🎓',
      'garden': '🌲', 'plant': '🌲',
      'beach': '🌊', 'surf': '🌊',
      'spiritual': '❤️', 'religion': '❤️', 'faith': '❤️',
      'social': '👥', 'community': '👥'
    };
    
    for (const [key, emoji] of Object.entries(emojiMap)) {
      if (hobbyLower.includes(key)) {
        return emoji;
      }
    }
    return '👥'; // Default emoji
  };

  const getUniqueHobbies = () => {
    const hobbies = new Set();
    groups.forEach(group => {
      if (Array.isArray(group.hobbies)) group.hobbies.forEach(h => { if (h) hobbies.add(h); });
      if (Array.isArray(group.tags)) group.tags.forEach(t => { if (t) hobbies.add(t); });
    });
    hangouts.forEach(h => {
      if (Array.isArray(h.tags)) h.tags.forEach(t => { if (t) hobbies.add(t); });
      if (h.category) hobbies.add(h.category);
    });
    events.forEach(ev => {
      if (Array.isArray(ev.tags)) ev.tags.forEach(t => { if (t) hobbies.add(t); });
      if (ev.category) hobbies.add(ev.category);
    });
    return Array.from(hobbies).sort();
  };

  const getHangoutCategoryLegendItems = () => {
    if (!hangouts || hangouts.length === 0) return [];
    const cats = new Map();
    hangouts.forEach(h => {
      if (!h.coordinates || h.coordinates.length !== 2) return;
      const cat = getHangoutCategory(h);
      cats.set(cat, (cats.get(cat) || 0) + 1);
    });
    return Array.from(cats.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([cat, count]) => ({
        category: cat,
        count,
        colors: EVENT_CATEGORY_COLORS[cat] || EVENT_CATEGORY_COLORS['Other'],
        iconSvg: EVENT_CATEGORY_ICONS[cat] || EVENT_CATEGORY_ICONS['Other'],
      }));
  };

  // Event icon legend items (only for events layer)
  const getEventIconLegendItems = () => {
    if (!events || events.length === 0) return [];
    const presentIcons = new Set();
    events.forEach((ev) => {
      const iconName = ev.tablerIconName || getEventTablerIconName(ev);
      if (iconName) presentIcons.add(iconName);
    });

    const ICON_META = {
      piano: { label: 'Jazz / Piano', color: '#ec4899' },
      'guitar-pick': { label: 'Guitar / Acoustic', color: '#ec4899' },
      microphone: { label: 'Vocals / Performance', color: '#f59e0b' },
      music: { label: 'Music', color: '#ec4899' },
      'ball-baseball': { label: 'Sports & Athletics', color: '#22c55e' },
      'ball-basketball': { label: 'Basketball', color: '#22c55e' },
      'ball-football': { label: 'Football / Soccer', color: '#22c55e' },
      'device-gamepad': { label: 'Gaming', color: '#6366f1' },
      movie: { label: 'Film & Cinema', color: '#a855f7' },
      camera: { label: 'Photography', color: '#a855f7' },
      palette: { label: 'Arts & Culture', color: '#a855f7' },
      'glass-cocktail': { label: 'Social Gathering', color: '#db2777' },
      'tools-kitchen': { label: 'Food & Dining', color: '#ef4444' },
      book: { label: 'Academic / Reading', color: '#14b8a6' },
      school: { label: 'Education & Workshop', color: '#14b8a6' },
      briefcase: { label: 'Career & Professional', color: '#0ea5e9' },
      'mood-smile': { label: 'Fun & Social', color: '#f59e0b' },
      heart: { label: 'Community Service', color: '#e11d48' },
      'heart-handshake': { label: 'Fundraiser & Charity', color: '#15803d' },
      users: { label: 'Club Meeting', color: '#0ea5e9' },
      confetti: { label: 'Festival & Fair', color: '#f97316' },
      trees: { label: 'Outdoor & Adventure', color: '#22c55e' },
      'map-pin': { label: 'Campus Event', color: '#06b6d4' },
      presentation: { label: 'Lecture / Seminar', color: '#6366f1' },
      'device-laptop': { label: 'Tech & Innovation', color: '#6366f1' },
      flag: { label: 'Activism & Advocacy', color: '#f97316' },
      cross: { label: 'Religion & Spirituality', color: '#4f46e5' },
      calendar: { label: 'General Event', color: '#6b7280' },
    };

    return EVENT_TABLER_ICON_TAGS
      .filter(({ name }) => presentIcons.has(name))
      .map(({ name }) => ({
        iconName: name,
        label: ICON_META[name]?.label || name,
        color: ICON_META[name]?.color || '#6b7280',
      }));
  };

  // Toggle hobby filter
  const toggleHobby = (hobby) => {
    setSelectedHobbies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(hobby)) {
        newSet.delete(hobby);
      } else {
        newSet.add(hobby);
      }
      return newSet;
    });
  };

  const toggleEventIcon = (iconName) => {
    setSelectedEventIcons((prev) => {
      const next = new Set(prev);
      if (next.has(iconName)) {
        next.delete(iconName);
      } else {
        next.add(iconName);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {effectiveShowMemoryPins ? 'My Pins' : mapLayer === 'all' ? 'Club Map' : mapLayer === 'clubs' ? 'Club Map' : mapLayer === 'hangouts' ? 'Hangouts Map' : 'Events Map'}
              </h2>
              {isLoadingLocation ? (
                <span className="text-xs text-gray-500">Locating you...</span>
              ) : userLocation ? (
                <span className="text-xs text-gray-500">
                  {user?.location?.city || ''}{user?.location?.city && user?.location?.state ? ', ' : ''}{user?.location?.state ? user.location.state.toUpperCase() : ''}
                  {!user?.location?.city && !user?.location?.state && 'Your location'}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {effectiveShowMemoryPins && (
              <>
                <button
                  type="button"
                  onClick={() => { setAddMemoryMode(false); setAddNoteMode((prev) => !prev); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${addNoteMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {addNoteMode ? 'Click map to place pin' : 'Add Note'}
                </button>
                <button
                  type="button"
                  onClick={() => { setAddNoteMode(false); setAddMemoryMode((prev) => !prev); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${addMemoryMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {addMemoryMode ? 'Click map to place pin' : 'Add memory'}
                </button>
              </>
            )}
            {allowPinsSwitch && (
              <button
                type="button"
                onClick={() => {
                  if (effectiveShowMemoryPins) {
                    setPinsMode(false);
                    setAddMemoryMode(false);
                    setAddNoteMode(false);
                    setPendingMemoryPin(null);
                    setPendingNotePin(null);
                  } else {
                    setPinsMode(true);
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${effectiveShowMemoryPins ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {effectiveShowMemoryPins ? 'Switch to Club Map' : 'Switch to Memory Pins'}
              </button>
            )}
            {!effectiveShowMemoryPins && (mapLayer === 'events' || mapLayer === 'all') && (
              <button
                onClick={() => setEventsRefreshKey((k) => k + 1)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                title="Refresh events"
              >
                Refresh
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close map"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative">
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <div className="text-gray-600">Loading map...</div>
            </div>
          )}

          {/* Legend - Bottom Right (hidden in memories mode) */}
          {!effectiveShowMemoryPins && (
          <div className="absolute bottom-4 right-4 z-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 max-h-[50vh] overflow-y-auto">
            {mapLayer === 'events' || (mapLayer === 'all' && events && events.length > 0) ? (
              <>
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1.5 px-1">
                  Filter events by icon
                </div>
                <div className="space-y-0.5 min-w-[160px] max-w-[200px]">
                  {getEventIconLegendItems().map(({ iconName, label, color }) => {
                    const isSelected = selectedEventIcons.has(iconName);
                    return (
                      <button
                        key={iconName}
                        onClick={() => toggleEventIcon(iconName)}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${
                          isSelected
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                            : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span
                          className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                          style={{
                            backgroundColor: color,
                            border: `2px solid ${color}`,
                            boxShadow: '0 6px 16px rgba(15,23,42,0.35)',
                          }}
                          dangerouslySetInnerHTML={{ __html: getTablerIconSvgByName(iconName, 16) }}
                        />
                        <span className="flex-1 truncate">{label}</span>
                        {isSelected && (
                          <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                  {getEventIconLegendItems().length === 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1.5">
                      No events available
                    </div>
                  )}
                  {selectedEventIcons.size > 0 && (
                    <button
                      onClick={() => setSelectedEventIcons(new Set())}
                      className="w-full text-left px-2 py-1.5 rounded-md text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 mt-1.5 border-t border-gray-200 dark:border-gray-700 pt-1.5"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </>
            ) : mapLayer === 'hangouts' ? (
              <>
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1.5 px-1">
                  Filter hangouts by type
                </div>
                <div className="space-y-0.5 min-w-[160px] max-w-[200px]">
                  {getHangoutCategoryLegendItems().map(({ category, count, colors: catColors, iconSvg: catIcon }) => {
                    const isSelected = selectedHangoutCats.has(category);
                    return (
                      <button
                        key={category}
                        onClick={() => {
                          setSelectedHangoutCats(prev => {
                            const next = new Set(prev);
                            if (next.has(category)) next.delete(category); else next.add(category);
                            return next;
                          });
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${
                          isSelected
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                            : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span
                          className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                          style={{
                            backgroundColor: catColors.bg,
                            border: `2px solid ${catColors.border}`,
                          }}
                          dangerouslySetInnerHTML={{ __html: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${catIcon}</svg>` }}
                        />
                        <span className="flex-1 truncate">{category}</span>
                        <span className="text-gray-400 text-[10px]">{count}</span>
                        {isSelected && (
                          <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                  {getHangoutCategoryLegendItems().length === 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1.5">
                      No hangouts available
                    </div>
                  )}
                  {selectedHangoutCats.size > 0 && (
                    <button
                      onClick={() => setSelectedHangoutCats(new Set())}
                      className="w-full text-left px-2 py-1.5 rounded-md text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 mt-1.5 border-t border-gray-200 dark:border-gray-700 pt-1.5"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1.5 px-1">
                  Filter by Hobby
                </div>
                <div className="space-y-0.5 min-w-[140px] max-w-[160px]">
                  {getUniqueHobbies().map(hobby => {
                    const isSelected = selectedHobbies.has(hobby);
                    return (
                      <button
                        key={hobby}
                        onClick={() => toggleHobby(hobby)}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${
                          isSelected
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="text-sm shrink-0">
                          <HobbyFilterIcon type={hobby} />
                        </span>
                        <span className="flex-1 truncate">{hobby}</span>
                        {isSelected && (
                          <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                  {getUniqueHobbies().length === 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1.5">
                      No hobbies available
                    </div>
                  )}
                  {selectedHobbies.size > 0 && (
                    <button
                      onClick={() => setSelectedHobbies(new Set())}
                      className="w-full text-left px-2 py-1.5 rounded-md text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 mt-1.5 border-t border-gray-200 dark:border-gray-700 pt-1.5"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          )}

          {/* Floating radius control (cleaner, more map space) */}
          <div className="absolute top-4 left-4 right-14 z-10">
            <div className="mx-auto max-w-2xl bg-white/90 backdrop-blur-md border border-gray-200 rounded-xl shadow-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-gray-800">
                  <Navigation className="h-4 w-4" />
                  <span className="text-sm font-semibold">Radius</span>
                </div>
                <button
                  onClick={() => getUserLocation(true)}
                  disabled={isLoadingLocation}
                  className="ml-auto px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Relocate using GPS"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isLoadingLocation ? 'animate-spin' : ''}`} />
                  Relocate
                </button>
                <div className="flex-1">
                  <input
                    type="range"
                    min="5"
                    max="200"
                    step="5"
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    className="w-full accent-blue-600"
                  />
                </div>
                <div className="text-sm font-bold text-gray-900 min-w-[64px] text-right">
                  {radius} km
                </div>
                <div className="hidden sm:block text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded-lg">
                  {effectiveShowMemoryPins
                    ? (() => {
                        const memCount = memoryFilter === 'all' ? memoryPins.length : memoryPins.filter(p => p.type === memoryFilter).length;
                        const noteCount = memoryFilter === 'all' ? notes.length : notes.filter(n => n.type === memoryFilter).length;
                        const parts = [];
                        if (memCount > 0) parts.push(`${memCount} ${memCount === 1 ? 'memory' : 'memories'}`);
                        if (noteCount > 0) parts.push(`${noteCount} ${noteCount === 1 ? 'note' : 'notes'}`);
                        return parts.length ? parts.join(', ') : '0 memories';
                      })()
                    : (() => {
                        const dataSet = mapLayer === 'all' ? [...groups, ...hangouts, ...events] : mapLayer === 'clubs' ? groups : mapLayer === 'hangouts' ? hangouts : events;
                        const within = dataSet.filter(g => {
                        if (!g.coordinates || !Array.isArray(g.coordinates) || g.coordinates.length !== 2 || !userLocation) return false;
                        const [groupLng, groupLat] = g.coordinates;
                        const [userLng, userLat] = userLocation;
                        const distance = calculateDistance(userLat, userLng, groupLat, groupLng);
                        return distance <= radius;
                        });
                        const pins = new Set(
                          within.map(g => `${Number(g.coordinates[0]).toFixed(4)},${Number(g.coordinates[1]).toFixed(4)}`)
                        ).size;
                        const label = mapLayer === 'all' ? 'items' : mapLayer === 'clubs' ? 'groups' : mapLayer === 'hangouts' ? 'hangouts' : 'events';
                        return `${within.length} ${label} • ${pins} pins`;
                      })()}
                </div>
              </div>
              {/* Memory type filter legend */}
              {effectiveShowMemoryPins && (
              <div className="flex items-center justify-center gap-1 mt-2">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'club', label: 'Club' },
                  { key: 'hangout', label: 'Hangouts' },
                  { key: 'event', label: 'Events' },
                ].map(({ key, label }) => {
                  const active = memoryFilter === key;
                  const colorMap = {
                    all: active ? 'bg-gray-800 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-gray-100',
                    club: active ? 'bg-blue-600 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-blue-50',
                    hangout: active ? 'bg-amber-500 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-amber-50',
                    event: active ? 'bg-emerald-600 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-emerald-50',
                  };
                  return (
                    <button
                      key={key}
                      onClick={() => setMemoryFilter(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${colorMap[key]}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              )}
              {/* Layer toggle: Clubs / Hangouts / Events (hidden in memories mode or locked layer) */}
              {!effectiveShowMemoryPins && defaultLayer === 'all' && (
              <div className="flex items-center justify-center gap-1 mt-2">
                {[
                  { key: 'all', label: 'All', icon: null, color: 'gray' },
                  { key: 'clubs', label: 'Clubs', icon: null, color: 'blue' },
                  { key: 'hangouts', label: 'Hangouts', icon: null, color: 'amber' },
                  { key: 'events', label: 'Events', icon: null, color: 'violet' },
                ].map(({ key, label, icon, color }) => {
                  const active = mapLayer === key;
                  const colorMap = {
                    gray: active ? 'bg-gray-900 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-gray-100',
                    blue: active ? 'bg-blue-600 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-blue-50',
                    amber: active ? 'bg-amber-500 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-amber-50',
                    violet: active ? 'bg-violet-600 text-white shadow-md' : 'bg-white/70 text-gray-700 hover:bg-violet-50',
                  };
                  return (
                    <button
                      key={key}
                      onClick={() => setMapLayer(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${colorMap[color]}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              )}
            </div>
          </div>

          <div className="relative w-full h-full">
          <div
            ref={mapContainer}
            className="w-full h-full"
            style={{ minHeight: '100%' }}
          />

          {/* Pending note pin marker (💭 - shown for Add Note only, not Add memory) */}
          {effectiveShowMemoryPins && pendingNotePin && pinPixel && (
            <span
              className="absolute z-20 text-2xl pointer-events-none drop-shadow-lg"
              style={{ left: pinPixel.x - 14, top: pinPixel.y - 14 }}
              aria-hidden
            >
              💭
            </span>
          )}

          {/* Add memory form overlay (flips above pin when near bottom) */}
          {effectiveShowMemoryPins && pendingMemoryPin && pinPixel && (() => {
            const formHeight = 320;
            const mapEl = mapContainer.current;
            const containerHeight = mapEl ? mapEl.clientHeight : (typeof window !== 'undefined' ? window.innerHeight : 800);
            const spaceBelow = containerHeight - pinPixel.y - 28;
            const placeAbove = spaceBelow < formHeight && pinPixel.y > formHeight;
            return (
            <div
              className="absolute z-30 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 w-52 max-h-[70vh] overflow-y-auto"
              style={{
                left: Math.max(8, Math.min(pinPixel.x - 104, typeof window !== 'undefined' ? window.innerWidth - 220 : pinPixel.x - 104)),
                ...(placeAbove
                  ? { bottom: containerHeight - pinPixel.y + 16 }
                  : { top: pinPixel.y + 28 }),
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => { setPendingMemoryPin(null); setNewPinMessage(''); setNewPinType('club'); setNewPinImage(null); }}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Type</div>
              <div className="flex gap-1.5 mb-2">
                {(['club', 'hangout', 'event']).map((t) => {
                  const cfg = memoryTypeConfig[t];
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNewPinType(t)}
                      className={`flex-1 py-1 rounded-md text-[10px] font-semibold border ${newPinType === t ? cfg.border : 'border-gray-200 dark:border-gray-600'} ${newPinType === t ? 'opacity-100' : 'opacity-60'}`}
                      style={{ background: cfg.color, color: 'white' }}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Short description</div>
              <textarea
                value={newPinMessage}
                onChange={(e) => setNewPinMessage(e.target.value)}
                placeholder="What happened here?"
                rows={2}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-md p-1.5 text-xs dark:bg-gray-700 dark:text-white mb-2 resize-none"
              />
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Photo</div>
              <div className="flex items-center gap-1.5 mb-2">
                <input
                  ref={memoryFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target?.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      let data = reader.result;
                      if (typeof data === 'string' && data.length > 500000) {
                        const img = new Image();
                        img.onload = () => {
                          const c = document.createElement('canvas');
                          const max = 800;
                          let w = img.width, h = img.height;
                          if (w > max || h > max) {
                            if (w > h) { h = (h * max) / w; w = max; } else { w = (w * max) / h; h = max; }
                          }
                          c.width = w; c.height = h;
                          c.getContext('2d').drawImage(img, 0, 0, w, h);
                          setNewPinImage(c.toDataURL('image/jpeg', 0.85));
                        };
                        img.src = data;
                      } else {
                        setNewPinImage(data);
                      }
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => memoryFileInputRef.current?.click()}
                  className="p-1.5 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1 text-[10px] font-medium"
                >
                  <Upload className="h-3 w-3" /> Upload
                </button>
                {newPinImage && (
                  <>
                    <img src={newPinImage} alt="" className="w-8 h-8 object-cover rounded-md" />
                    <button type="button" onClick={() => setNewPinImage(null)} className="text-[10px] text-red-600 hover:underline">Clear</button>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = [...memoryPins, {
                    id: Date.now().toString(),
                    lng: pendingMemoryPin.lng,
                    lat: pendingMemoryPin.lat,
                    type: newPinType,
                    message: newPinMessage.trim() || null,
                    imageData: newPinImage || undefined,
                    createdAt: new Date().toISOString(),
                  }];
                  setMemoryPins(next);
                  saveMemoryPins(userId, next);
                  setPendingMemoryPin(null);
                  setNewPinMessage('');
                  setNewPinType('club');
                  setNewPinImage(null);
                }}
                className="w-full py-1.5 bg-blue-600 text-white rounded-md font-semibold text-xs hover:bg-blue-700"
              >
                Add pin
              </button>
            </div>
            );
          })()}

          {/* Add note form overlay (Type + Note only, no upload) */}
          {effectiveShowMemoryPins && pendingNotePin && pinPixel && (() => {
            const formHeight = 180;
            const mapEl = mapContainer.current;
            const containerHeight = mapEl ? mapEl.clientHeight : (typeof window !== 'undefined' ? window.innerHeight : 800);
            const spaceBelow = containerHeight - pinPixel.y - 28;
            const placeAbove = spaceBelow < formHeight && pinPixel.y > formHeight;
            return (
            <div
              className="absolute z-30 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 w-52 max-h-[70vh] overflow-y-auto"
              style={{
                left: Math.max(8, Math.min(pinPixel.x - 104, typeof window !== 'undefined' ? window.innerWidth - 220 : pinPixel.x - 104)),
                ...(placeAbove
                  ? { bottom: containerHeight - pinPixel.y + 16 }
                  : { top: pinPixel.y + 28 }),
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => { setPendingNotePin(null); setNewNoteType('club'); setNewNoteText(''); }}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Type</div>
              <div className="flex gap-1.5 mb-2">
                {(['club', 'hangout', 'event']).map((t) => {
                  const cfg = memoryTypeConfig[t];
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNewNoteType(t)}
                      className={`flex-1 py-1 rounded-md text-[10px] font-semibold border ${newNoteType === t ? cfg.border : 'border-gray-200 dark:border-gray-600'} ${newNoteType === t ? 'opacity-100' : 'opacity-60'}`}
                      style={{ background: cfg.color, color: 'white' }}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Note</div>
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Add your note..."
                rows={3}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-md p-1.5 text-xs dark:bg-gray-700 dark:text-white mb-2 resize-none"
              />
              <button
                type="button"
                onClick={() => {
                  const next = [...notes, {
                    id: Date.now().toString(),
                    lng: pendingNotePin.lng,
                    lat: pendingNotePin.lat,
                    type: newNoteType,
                    text: newNoteText.trim() || null,
                    createdAt: new Date().toISOString(),
                  }];
                  setNotes(next);
                  saveNotes(userId, next);
                  setPendingNotePin(null);
                  setNewNoteType('club');
                  setNewNoteText('');
                  setAddNoteMode(false);
                }}
                className="w-full py-1.5 bg-blue-600 text-white rounded-md font-semibold text-xs hover:bg-blue-700"
              >
                Add note
              </button>
            </div>
            );
          })()}

          </div>

          {/* Event chat panel – temporary chat for everyone going to this event */}
          {eventChat && (
            <div className="absolute top-20 right-4 z-20 bg-white dark:bg-[#1f2933] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-80 flex flex-col max-h-[80vh]">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg" aria-hidden>💬</span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">{eventChat.eventTitle}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Chat with others going to this event</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setEventChat(null); setEventChatMessages([]); setEventChatInput(''); }}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors shrink-0"
                  aria-label="Close chat"
                >
                  <X className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {eventChat.ended && (
                <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs font-medium border-b border-amber-200 dark:border-amber-800">
                  Event ended. Chat is read-only.
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                {eventChatMessages.length === 0 && !eventChatLoading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No messages yet. Say hi!</p>
                )}
                {eventChatMessages.map((msg) => (
                  <div key={msg.id || msg.timestamp} className="flex flex-col gap-0.5">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{msg.senderName || 'Someone'}</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 break-words">{msg.text}</p>
                  </div>
                ))}
                <div ref={eventChatEndRef} />
              </div>
              {!eventChat.ended && (
                <form
                  className="p-3 border-t border-gray-200 dark:border-gray-700 shrink-0"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const text = eventChatInput.trim();
                    if (!text || eventChatLoading) return;
                    setEventChatLoading(true);
                    setEventChatInput('');
                    try {
                      await api.post('/messages', { chatId: eventChat.chatId, text });
                      const res = await api.get('/messages', { params: { chatId: eventChat.chatId } });
                      setEventChatMessages(res.data || []);
                    } catch (err) {
                      console.warn('Send event chat message failed', err);
                    } finally {
                      setEventChatLoading(false);
                    }
                  }}
                >
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={eventChatInput}
                      onChange={(e) => setEventChatInput(e.target.value)}
                      placeholder="Message everyone at this event..."
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={eventChatLoading}
                    />
                    <button
                      type="submit"
                      disabled={eventChatLoading || !eventChatInput.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Directions Panel */}
          {selectedDestination && (
            <div className="absolute top-20 right-4 z-20 bg-white dark:bg-[#1f2933] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-80 max-h-[80vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Directions</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={recenterMap}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                      title="Recenter map"
                    >
                      <Navigation className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                      onClick={clearRoute}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                    >
                      <XCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedDestination.name}</p>
                  <p className="text-xs mt-1">
                    {selectedDestination.location?.city || ''}, {selectedDestination.location?.state || ''}
                  </p>
                </div>
              </div>

              {/* Mode Selector */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => setRouteMode('driving')}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      routeMode === 'driving'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Car className="h-5 w-5" />
                    <span className="text-xs font-semibold">Car</span>
                  </button>
                  <button
                    onClick={() => setRouteMode('walking')}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      routeMode === 'walking'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Footprints className="h-5 w-5" />
                    <span className="text-xs font-semibold">Walk</span>
                  </button>
                  <button
                    onClick={() => setRouteMode('cycling')}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      routeMode === 'cycling'
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Bike className="h-5 w-5" />
                    <span className="text-xs font-semibold">Cycle</span>
                  </button>
                  <button
                    onClick={() => setRouteMode('transit')}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      routeMode === 'transit'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Bus className="h-5 w-5" />
                    <span className="text-xs font-semibold">Bus</span>
                  </button>
                </div>
              </div>

              {/* ETAs for all modes */}
              <div className="p-4">
                {isLoadingRoute ? (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm">Calculating route...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Estimated Time
                    </div>
                    {etas.driving && (
                      <div className={`p-3 rounded-lg border-2 ${
                        routeMode === 'driving'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Car className={`h-4 w-4 ${routeMode === 'driving' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">Car</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">{etas.driving.time}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{etas.driving.distance} km</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {etas.walking && (
                      <div className={`p-3 rounded-lg border-2 ${
                        routeMode === 'walking'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Footprints className={`h-4 w-4 ${routeMode === 'walking' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`} />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">Walk</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">{etas.walking.time}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{etas.walking.distance} km</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {etas.cycling && (
                      <div className={`p-3 rounded-lg border-2 ${
                        routeMode === 'cycling'
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Bike className={`h-4 w-4 ${routeMode === 'cycling' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`} />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">Cycle</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">{etas.cycling.time}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{etas.cycling.distance} km</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {etas.transit && (
                      <div className={`p-3 rounded-lg border-2 ${
                        routeMode === 'transit'
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Bus className={`h-4 w-4 ${routeMode === 'transit' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">Bus</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">{etas.transit.time}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{etas.transit.distance} km</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {!etas.driving && !etas.walking && !etas.cycling && !etas.transit && (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                        No route available
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorldMapCopy;
